import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BarChart3, Store, Users, Package, FolderOpen, CreditCard, Droplets,
  Coins, ShoppingCart, Tag, Banknote, Building2, AlertTriangle, XCircle, CheckCircle,
  Printer, Medal, Award, Trophy, TrendingUp, Calendar, Clock, RefreshCw,
  UserCheck, Grid3X3, Wrench, Gem, Wallet, GitCompareArrows, Megaphone, Menu, X,
  ArrowUpDown, ArrowUp, ArrowDown, Receipt, Download, Scale, Send,
  ChevronRight, ChevronDown, ChevronLeft, SlidersHorizontal
} from 'lucide-react';
import { API_URL, API_HOST } from '../config/api';
import { exportReportToExcel, exportSectionedReportToExcel } from '../utils/reportExport';
import { eTicaretGelirRows, eTicaretNetRows, eTicaretSummary, eTicaretCardLabel } from '../utils/kasaOzeti';
import Loading from '../components/Loading';
import useEscapeClose from '../hooks/useEscapeClose';
import CustomerAnalysis from './report-tabs/CustomerAnalysis';
import AgingInventory from './report-tabs/AgingInventory';
import SalesHeatmap from './report-tabs/SalesHeatmap';
import RepairReport from './report-tabs/RepairReport';
import ScrapReport from './report-tabs/ScrapReport';
import ExpenseAnalysis from './report-tabs/ExpenseAnalysis';
import StoreComparison from './report-tabs/StoreComparison';
import TrendAnalysis from './report-tabs/TrendAnalysis';
import CampaignPerformance from './report-tabs/CampaignPerformance';
import CustomReport from './report-tabs/CustomReport';
import './Reports.css';
import './RetailSales.css';

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const Reports = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [monthOffset, setMonthOffset] = useState(0); // 0 = bu ay, -1 = geçen ay, +1 = sonraki ay
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });

  const getMonthTarget = useCallback(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  }, [monthOffset]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Raporlar hazırlanıyor...');

  // Büyük/tüm-tabloyu çeken uçlar (sales?all=true, exchanges?all=true, scrap-purchases,
  // customers/debts/all) sekme/tarih değiştirmede tekrar çekilmesin diye önbelleğe alınır.
  // Sadece ilk girişte bir kez indirilir; sayfa fiziksel olarak yenilenirse (F5) tazelenir.
  const heavyDataCacheRef = useRef({});
  const fetchHeavyCached = useCallback(async (key, url) => {
    if (heavyDataCacheRef.current[key]) return heavyDataCacheRef.current[key];
    const res = await fetch(url);
    const data = await res.json();
    heavyDataCacheRef.current[key] = data;
    return data;
  }, []);

  // Data states
  const [sales, setSales] = useState([]);
  const [stores, setStores] = useState([]);
  const [staff, setStaff] = useState([]);
  // Plasiyer Raporu'nda satışı doğru kişiye bağlamak için: sales.staff_id eski/bayat bir id
  // olabiliyor (sales_staff tablosu yeniden numaralandırılmış), staff_name de bazen mağaza
  // eki olmadan ("Celal Tarık Basım") bazen ekli ("Celal Tarık Basım (Osmangazi)") kaydedilmiş
  // olabiliyor. employees.id de tarihsel olarak sales.staff_id'de saklanmış olabildiğinden
  // (bkz. employees-sales-performance endpoint'i) her iki id uzayını da eşleştirmede kullanıyoruz.
  const [employeesForStaffMatch, setEmployeesForStaffMatch] = useState([]);
  const [productStats, setProductStats] = useState([]);
  const [productSortField, setProductSortField] = useState(null);
  const [productSortDir, setProductSortDir] = useState('desc');
  const [categoryStats, setCategoryStats] = useState([]);
  const [notSoldProducts, setNotSoldProducts] = useState([]);
  const [notSoldDays, setNotSoldDays] = useState(7);
  const [reportImageModal, setReportImageModal] = useState(null);
  const [consumableStats, setConsumableStats] = useState(null);
  const [consumableLoading, setConsumableLoading] = useState(false);

  // Mağaza filtresi
  const [reportStoreFilter, setReportStoreFilter] = useState('all');

  // Sol menü
  const [reportMenuOpen, setReportMenuOpen] = useState(false);

  // Personel detay modal
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);

  // Personel performansı - backend'den (Personnel sayfası prim hesabıyla aynı kaynak)
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [staffPerformanceLoading, setStaffPerformanceLoading] = useState(false);

  // Gün sonu raporları state
  const [endOfDayReports, setEndOfDayReports] = useState({});
  const [endOfDayLoading, setEndOfDayLoading] = useState(false);
  const [endOfDayDate, setEndOfDayDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedEodStore, setSelectedEodStore] = useState('all'); // 'all' veya store_id

  // Kasa Raporu state — dönem ve mağaza filtresi üstteki global seçiciden gelir
  const [cashReportData, setCashReportData] = useState(null);
  const [cashReportLoading, setCashReportLoading] = useState(false);
  // Tamirat geliri (overview + stores için) — Kasa Raporu ile birebir kanonik formül
  const [repairRevenue, setRepairRevenue] = useState({ total: 0, byStore: {} });

  // Kasiyer detaylı rapor: ürün detay dropdown'ı için açık fiş ID'leri
  const [expandedStaffSaleIds, setExpandedStaffSaleIds] = useState(() => new Set());
  const toggleStaffSaleExpand = useCallback((key) => {
    setExpandedStaffSaleIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showStaffModal, () => setShowStaffModal(false)],
  ]);

  const periods = [
    { id: 'today', name: 'Bugün' },
    { id: 'yesterday', name: 'Dün' },
    { id: 'week', name: 'Bu Hafta' },
    { id: 'month', name: 'Bu Ay' },
    { id: 'quarter', name: 'Bu Çeyrek' },
    { id: 'year', name: 'Bu Yıl' },
    { id: 'alltime', name: 'Tüm Zamanlar' },
    { id: 'custom', name: 'Özel Tarih' },
  ];

  const tabs = [
    { id: 'overview', name: 'Genel Bakış', icon: <BarChart3 size={18} /> },
    { id: 'customReport', name: 'Özel Rapor', icon: <SlidersHorizontal size={18} /> },
    { id: 'stores', name: 'Mağaza Raporları', icon: <Store size={18} /> },
    { id: 'staff', name: 'Personel Performansı', icon: <Users size={18} /> },
    { id: 'staffSales', name: 'Plasiyer Raporu', icon: <Receipt size={18} /> },
    { id: 'cashReport', name: 'Kasa Raporu', icon: <Wallet size={18} /> },
    { id: 'cashReportSummary', name: 'Kasa Raporu Özet', icon: <Wallet size={18} /> },
    { id: 'products', name: 'Ürün Analizi', icon: <Package size={18} /> },
    { id: 'categories', name: 'Kategori Analizi', icon: <FolderOpen size={18} /> },
    { id: 'payments', name: 'Ödeme Yöntemleri', icon: <CreditCard size={18} /> },
    { id: 'consumables', name: 'Sarf Malzemeleri', icon: <Droplets size={18} /> },
    { id: 'customers', name: 'Müşteri Analizi', icon: <UserCheck size={18} /> },
    { id: 'aging', name: 'Yaşlanan Stok', icon: <Clock size={18} /> },
    { id: 'heatmap', name: 'Yoğunluk Haritası', icon: <Grid3X3 size={18} /> },
    { id: 'repairs', name: 'Tamir Servisi', icon: <Wrench size={18} /> },
    { id: 'scrap', name: 'Hurda Alım', icon: <Gem size={18} /> },
    { id: 'expenses', name: 'Gider Analizi', icon: <Wallet size={18} /> },
    { id: 'comparison', name: 'Mağaza Karşılaştırma', icon: <GitCompareArrows size={18} /> },
    { id: 'trends', name: 'Trend Analizi', icon: <TrendingUp size={18} /> },
    { id: 'campaigns', name: 'Kampanya Performansı', icon: <Megaphone size={18} /> },
  ];

  // Rapor çıktısı için döneme/mağaza filtresine okunabilir etiket üret
  const getPeriodLabel = useCallback(() => {
    const fmt = (d) => new Date(d).toLocaleDateString('tr-TR');
    if (selectedPeriod === 'custom' && customDateRange.start && customDateRange.end) {
      return `${fmt(customDateRange.start)} — ${fmt(customDateRange.end)}`;
    }
    if (selectedPeriod === 'month') {
      const t = getMonthTarget();
      return `${TR_MONTHS[t.getMonth()]} ${t.getFullYear()}`;
    }
    const periodObj = periods.find(p => p.id === selectedPeriod);
    return periodObj ? periodObj.name : '-';
  }, [selectedPeriod, customDateRange, getMonthTarget]);

  const getStoreLabel = useCallback(() => {
    if (reportStoreFilter === 'all') return 'Tümü';
    if (reportStoreFilter === 'gold') return 'Akik Gold';
    if (reportStoreFilter === 'silver') return 'Gümüş Mağazaları';
    const store = stores.find(s => String(s.id) === String(reportStoreFilter));
    return store ? store.name : '-';
  }, [reportStoreFilter, stores]);

  // Tarih aralığını hesapla
  const getDateRange = useCallback(() => {
    const now = new Date();
    let startDate, endDate = now;

    switch (selectedPeriod) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month': {
        const target = getMonthTarget();
        startDate = new Date(target.getFullYear(), target.getMonth(), 1);
        if (monthOffset !== 0) {
          endDate = new Date(target.getFullYear(), target.getMonth() + 1, 0, 23, 59, 59);
        }
        break;
      }
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'alltime':
        startDate = new Date(2020, 0, 1);
        break;
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          startDate = new Date(customDateRange.start);
          endDate = new Date(customDateRange.end);
        } else {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    // Yerel tarih bileşenlerinden YYYY-MM-DD üret (toISOString UTC'ye çevirip off-by-one yapıyordu)
    const toLocalYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      start: toLocalYMD(startDate),
      end: toLocalYMD(endDate)
    };
  }, [selectedPeriod, customDateRange, monthOffset, getMonthTarget]);

  // Kasa Defteri ledger hesabı — "Kasa Raporu Özet" ile BİREBİR aynı formüller.
  // Hem Kasa Raporu hem Excel export tek kaynaktan beslenir; Özet'in inline koduna dokunulmaz.
  const buildCashLedger = useCallback((r) => {
    if (!r) return null;
    const num = (v) => Number(v) || 0;

    // (+) GELİRLER
    const gelir = [];
    const nakitOdeme = r.cash?.salesTotal != null ? num(r.cash.salesTotal) : (num(r.cash?.total) - num(r.cash?.collectionTotal) - num(r.cash?.repairTotal));
    if (nakitOdeme > 0) gelir.push(['Nakit Ödeme', nakitOdeme]);
    if (num(r.cash?.collectionTotal) > 0) gelir.push(['Nakit Tahsilat', num(r.cash.collectionTotal)]);
    if (num(r.cash?.repairTotal) > 0) gelir.push(['Nakit Tamirat', num(r.cash.repairTotal)]);
    eTicaretGelirRows(r.sanalPos).forEach((row) => gelir.push(row));
    if (num(r.card?.salesTotal ?? r.card?.total) > 0) gelir.push(['Kredi Kartı', num(r.card?.salesTotal ?? r.card?.total)]);
    if (num(r.card?.collectionTotal) > 0) gelir.push(['Kredi Kartı Tahsilat', num(r.card.collectionTotal)]);
    if (num(r.card?.repairTotal) > 0) gelir.push(['Kredi Kartı Tamirat', num(r.card.repairTotal)]);

    const tSalesTotal = num(r.transfer?.salesTotal ?? r.transfer?.total);
    const byAcc = r.transfer?.byAccount || {};
    const accNames = Object.keys(byAcc).filter(n => byAcc[n].total > 0);
    if (tSalesTotal > 0 && accNames.length) {
      accNames.forEach(name => gelir.push([`Havale/EFT ${name.replace('HAVALE ', '')}`, num(byAcc[name].total)]));
    } else if (tSalesTotal > 0) {
      gelir.push(['Havale/EFT', tSalesTotal]);
    }
    const tCollTotal = num(r.transfer?.collectionTotal);
    const collByAcc = r.transfer?.collectionByAccount || {};
    const collAccNames = Object.keys(collByAcc).filter(n => collByAcc[n].total > 0);
    if (tCollTotal > 0 && collAccNames.length) {
      collAccNames.forEach(name => gelir.push([`Tahsilat ${name.replace('HAVALE ', '')}`, num(collByAcc[name].total)]));
    } else if (tCollTotal > 0) {
      gelir.push(['Havale/EFT Tahsilat', tCollTotal]);
    }
    if (num(r.transfer?.repairTotal) > 0) gelir.push(['Havale/EFT Tamirat', num(r.transfer.repairTotal)]);
    if (num(r.voucher?.total) > 0) gelir.push(['Bakiye', num(r.voucher.total)]);
    if (num(r.scrap?.saleLinkedGoldTotal) > 0) gelir.push(['Altın Hurda', num(r.scrap.saleLinkedGoldTotal)]);
    if (num(r.scrap?.saleLinkedSilverTotal) > 0) gelir.push(['Gümüş Hurda', num(r.scrap.saleLinkedSilverTotal)]);
    if (num(r.collections?.hurdaTotal) > 0) gelir.push(['Hurda ile Tahsilat', num(r.collections.hurdaTotal)]);

    const gelirTop =
      num(r.cash?.total) + num(r.sanalPos?.total) +
      num(r.card?.salesTotal ?? r.card?.total) + num(r.card?.collectionTotal) + num(r.card?.repairTotal) +
      num(r.transfer?.salesTotal ?? r.transfer?.total) + num(r.transfer?.collectionTotal) + num(r.transfer?.repairTotal) +
      num(r.voucher?.total) + num(r.scrap?.saleLinkedTotal) + num(r.collections?.hurdaTotal);

    // (-) GİDERLER
    const giderEntries = [
      ['Kredi Kartı İade', num(r.cardRefund?.total)],
      ['Nakit İade', num(r.cashRefund?.total)],
      ['Hurda Nakit İadesi', num(r.scrapCashRefund?.total)],
      ['Sanal Pos İade', num(r.sanalPosRefund?.total)],
      ['Değişim Nakit İade', num(r.exchanges?.cashRefundTotal)],
      ['Değişim Kredi Kartı İade', num(r.exchanges?.cardRefundTotal)],
      ['Havale İade', num(r.havaleRefund?.total)],
      ['Değişim Havale İade', num(r.exchanges?.havaleRefundTotal)],
      ['Masraflar', num(r.expenses?.total)],
      ['Hurda Alımları', num(r.scrap?.standaloneTotal)]
    ];
    const gider = giderEntries.filter(([, v]) => v > 0).map(([label, v]) => [label, v]);
    const giderTop = giderEntries.reduce((s, [, v]) => s + (v > 0 ? v : 0), 0);

    // Alt detaylar (net dağılımlar)
    const transferByAccount = r.transfer?.byAccount || {};
    const collectionByAccount = r.transfer?.collectionByAccount || {};
    const repairByAccount = r.transfer?.repairByAccount || {};
    const scrapByAccount = r.scrap?.transferByAccount || {};
    const allAccountNames = [...new Set([...Object.keys(transferByAccount), ...Object.keys(collectionByAccount), ...Object.keys(repairByAccount), ...Object.keys(scrapByAccount)])];
    let netHavaleSirket = 0, netHavaleSahis = 0, netHavaleETicaret = 0;
    allAccountNames.forEach(acc => {
      const accGelir = (transferByAccount[acc]?.total ?? 0) + (collectionByAccount[acc]?.total ?? 0) + (repairByAccount[acc]?.total ?? 0);
      const accGider = scrapByAccount[acc]?.total ?? 0;
      const accNet = accGelir - accGider;
      const accLower = acc.toLocaleLowerCase('tr');
      if (accLower.includes('şahıs') || accLower.includes('sahis') || acc.includes('(Şahıs)')) netHavaleSahis += accNet;
      else if (accLower.includes('e-ticaret') || accLower.includes('eticaret') || acc.includes('E-TİCARET')) netHavaleETicaret += accNet;
      else netHavaleSirket += accNet;
    });
    const netKrediKarti = (num(r.card?.salesTotal) + num(r.card?.collectionTotal) + num(r.card?.repairTotal)) - (num(r.cardRefund?.total) + num(r.exchanges?.cardRefundTotal) + num(r.scrap?.cardTotal));
    const toplamHurda = num(r.scrap?.total);
    const netHavaleToplam = netHavaleSahis + netHavaleSirket + netHavaleETicaret;

    return {
      gelir, gelirTop, gider, giderTop,
      kasaGenel: gelirTop - giderTop,
      netKrediKarti, netHavaleSahis, netHavaleSirket, netHavaleETicaret, netHavaleToplam, toplamHurda
    };
  }, []);

  // Verileri yükle
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingProgress(0);
      setLoadingText('Raporlar hazırlanıyor...');

      // Animasyonlu progress: hedef değere yumuşak geçiş
      let currentProgress = 0;
      const animateTo = (target) => new Promise(resolve => {
        const step = () => {
          currentProgress += Math.max(1, Math.ceil((target - currentProgress) / 4));
          if (currentProgress >= target) { currentProgress = target; setLoadingProgress(target); resolve(); return; }
          setLoadingProgress(currentProgress);
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });

      try {
        // 1. Satış verileri (%0→8)
        setLoadingText('Satış verileri isteniyor...');
        await animateTo(8);

        // 2. Satış verileri parse (%8→30)
        setLoadingText('Satış verileri okunuyor...');
        await animateTo(18);
        const salesData = await fetchHeavyCached('sales', `${API_URL}/sales?all=true`);
        await animateTo(30);

        // 3. Değişim/iade verileri (%30→38)
        setLoadingText('Değişim ve iade verileri isteniyor...');
        await animateTo(35);

        // 4. Değişim/iade parse (%38→50)
        setLoadingText('Değişim ve iade verileri okunuyor...');
        await animateTo(42);
        const exchangesData = await fetchHeavyCached('exchanges', `${API_URL}/exchanges?all=true`);
        await animateTo(50);

        // 5. Mağaza verileri (%50→58)
        setLoadingText('Mağaza verileri yükleniyor...');
        await animateTo(54);
        const storesRes = await fetch(`${API_URL}/stores`);
        const storesData = await storesRes.json();
        await animateTo(58);

        // 6. Kullanıcı verileri (%58→66)
        setLoadingText('Kullanıcı verileri yükleniyor...');
        await animateTo(62);
        const usersRes = await fetch(`${API_URL}/users`);
        const usersData = await usersRes.json();
        await animateTo(66);

        // 7. Personel verileri (%66→74)
        setLoadingText('Personel verileri yükleniyor...');
        await animateTo(70);
        const salesStaffRes = await fetch(`${API_URL}/sales-staff`);
        const salesStaffData = await salesStaffRes.json();
        const employeesRes = await fetch(`${API_URL}/employees`);
        const employeesData = await employeesRes.json();
        setEmployeesForStaffMatch(Array.isArray(employeesData) ? employeesData : []);
        await animateTo(74);

        // 8. Satışları hazırla (%74→82)
        setLoadingText('Satış verileri işleniyor...');
        const salesWithType = Array.isArray(salesData) ? salesData.map(sale => ({
          ...sale,
          record_type: sale.sale_type === 'refund' ? 'refund' : 'sale'
        })) : [];
        await animateTo(82);

        // 9. Değişim/iadeleri dönüştür (%82→90)
        setLoadingText('Değişim verileri işleniyor...');
        const exchanges = Array.isArray(exchangesData) ? exchangesData.map(exc => ({
          id: exc.receipt_number || `E${exc.id}`,
          exchange_id: exc.id,
          created_at: exc.created_at,
          customer_name: exc.customer_name,
          total: exc.difference,
          payment_method: exc.payment_method,
          status: exc.type === 'refund' ? 'refund_completed' : 'exchange_completed',
          store_id: exc.store_id,
          staff_name: exc.staff_name,
          record_type: exc.type,
          original_sale_id: exc.original_sale_id,
          new_sale_id: exc.new_sale_id,
          return_items: exc.return_items,
          new_items: exc.new_items,
          return_total: exc.return_total,
          new_items_total: exc.new_items_total,
          receipt_number: exc.receipt_number,
          new_sale_receipt_number: exc.new_sale_receipt_number,
          items: exc.return_items || []
        })) : [];
        await animateTo(90);

        // 10. Birleştir ve sırala (%90→96)
        setLoadingText('Veriler birleştiriliyor...');
        const combined = [...salesWithType, ...exchanges].sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setSales(combined);
        setStores(Array.isArray(storesData) ? storesData : []);
        await animateTo(96);

        // 11. Personel listesi (%96→100)
        setLoadingText('Son düzenlemeler yapılıyor...');
        const allStaff = [];
        if (Array.isArray(salesStaffData)) {
          salesStaffData.forEach(s => {
            allStaff.push({
              id: `staff_${s.id}`,
              name: s.name,
              role: 'staff',
              type: 'sales_staff'
            });
          });
        }
        setStaff(allStaff);
        await animateTo(100);
        setLoadingText('Hazır!');
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
      } finally {
        setTimeout(() => setLoading(false), 400);
      }
    };
    fetchData();
  }, []);

  // Ürün ve kategori istatistiklerini yükle (dönem veya mağaza değiştiğinde)
  useEffect(() => {
    const fetchReportData = async () => {
      try {
        const dateRange = getDateRange();
        let dateParams = `start_date=${dateRange.start}&end_date=${dateRange.end}`;
        if (reportStoreFilter === 'gold') dateParams += '&store_id=1';
        else if (reportStoreFilter === 'silver') dateParams += '&store_ids=2,3';
        else if (reportStoreFilter !== 'all') dateParams += `&store_id=${reportStoreFilter}`;

        const [productRes, categoryRes] = await Promise.all([
          fetch(`${API_URL}/reports/product-stats?${dateParams}`),
          fetch(`${API_URL}/reports/category-stats?${dateParams}`)
        ]);

        if (productRes.ok) {
          const productData = await productRes.json();
          setProductStats(Array.isArray(productData) ? productData : []);
        }

        if (categoryRes.ok) {
          const categoryData = await categoryRes.json();
          setCategoryStats(Array.isArray(categoryData) ? categoryData : []);
        }
      } catch (error) {
        console.error('Rapor verisi yükleme hatası:', error);
      }
    };

    fetchReportData();
  }, [selectedPeriod, customDateRange, reportStoreFilter, monthOffset]);

  // Uzun süredir satılmayan ürünleri yükle
  useEffect(() => {
    const fetchNotSoldProducts = async () => {
      try {
        const res = await fetch(`${API_URL}/reports/not-sold-products?days=${notSoldDays}`);
        if (res.ok) {
          const data = await res.json();
          setNotSoldProducts(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Satılmayan ürünler yükleme hatası:', error);
      }
    };

    fetchNotSoldProducts();
  }, [notSoldDays]);

  // Personel performansı: Personnel sayfasındaki prim hesabıyla aynı backend mantığını kullan
  useEffect(() => {
    const fetchStaffPerformance = async () => {
      if (activeTab !== 'staff') return;
      const range = getDateRange();
      if (!range.start || !range.end) return;

      setStaffPerformanceLoading(true);
      try {
        const params = new URLSearchParams({
          start_date: range.start,
          end_date: range.end,
          store_filter: reportStoreFilter
        });
        const res = await fetch(`${API_URL}/employees-sales-performance?${params}`);
        if (res.ok) {
          const data = await res.json();
          setStaffPerformance(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Personel performans yükleme hatası:', error);
      } finally {
        setStaffPerformanceLoading(false);
      }
    };

    fetchStaffPerformance();
  }, [activeTab, selectedPeriod, customDateRange, reportStoreFilter, getDateRange]);

  // Tamirat geliri (Genel Bakış + Mağaza Raporları + Plasiyer Raporu) — Kasa Raporu ile tutarlı
  useEffect(() => {
    const fetchRepairRevenue = async () => {
      if (activeTab !== 'overview' && activeTab !== 'stores' && activeTab !== 'staffSales') return;
      const range = getDateRange();
      if (!range.start || !range.end) return;
      try {
        const params = new URLSearchParams({
          start_date: range.start,
          end_date: range.end,
          store_filter: reportStoreFilter
        });
        const res = await fetch(`${API_URL}/reports/repair-revenue?${params}`);
        if (res.ok) {
          const data = await res.json();
          setRepairRevenue({ total: Number(data.total) || 0, byStore: data.byStore || {} });
        }
      } catch (error) {
        console.error('Tamirat geliri yükleme hatası:', error);
      }
    };

    fetchRepairRevenue();
  }, [activeTab, selectedPeriod, customDateRange, reportStoreFilter, getDateRange]);

  // Sarf malzemeleri istatistiklerini yükle
  useEffect(() => {
    const fetchConsumableStats = async () => {
      if (activeTab !== 'consumables') return;

      setConsumableLoading(true);
      try {
        const res = await fetch(`${API_URL}/reports/consumables`);
        if (res.ok) {
          const data = await res.json();
          setConsumableStats(data);
        }
      } catch (error) {
        console.error('Sarf malzemeleri yükleme hatası:', error);
      } finally {
        setConsumableLoading(false);
      }
    };

    fetchConsumableStats();
  }, [activeTab]);

  // Kasa Raporu fetch (hem 'cashReport' hem 'cashReportSummary' için ortak)
  // Dönem ve mağaza filtresi üstteki global seçiciden alınır
  const fetchCashReport = useCallback(async () => {
    if (activeTab !== 'cashReport' && activeTab !== 'cashReportSummary') return;

    // Global mağaza filtresini store_ids listesine çevir
    let storeIds;
    if (reportStoreFilter === 'all') {
      storeIds = stores.map(s => s.id).join(',');
    } else if (reportStoreFilter === 'gold') {
      storeIds = '1';
    } else if (reportStoreFilter === 'silver') {
      storeIds = '2,3';
    } else {
      storeIds = String(reportStoreFilter);
    }
    if (!storeIds) return; // mağazalar henüz yüklenmediyse

    const { start: startDate, end: endDate } = getDateRange();
    if (!startDate || !endDate) return;

    setCashReportLoading(true);
    try {
      const res = await fetch(`${API_URL}/daily-report-range?store_ids=${storeIds}&start_date=${startDate}&end_date=${endDate}`);
      if (!res.ok) throw new Error('Rapor alınamadı');
      const data = await res.json();
      setCashReportData(data);
    } catch (err) {
      console.error('Kasa raporu hatası:', err);
      setCashReportData(null);
    } finally {
      setCashReportLoading(false);
    }
  }, [activeTab, reportStoreFilter, stores, getDateRange]);

  useEffect(() => {
    fetchCashReport();
  }, [fetchCashReport]);

  // Gün sonu raporlarını yükle
  useEffect(() => {
    const fetchEndOfDayReports = async () => {
      if (activeTab !== 'endofday') return;
      if (stores.length === 0) return; // Mağazalar yüklenmediyse bekle

      setEndOfDayLoading(true);
      try {
        const selectedDate = new Date(endOfDayDate);
        selectedDate.setHours(0, 0, 0, 0);

        // Tüm mağazalar için veri çek — büyük/tüm-tablo uçları önbellekten (sadece ilk seferde ağdan çekilir),
        // sadece tarihe göre filtrelenen uçlar (expenses/debt-payments/repair-payments) her tarih değişiminde tazelenir
        const [allSales, allDebts, allScrap, allExchanges, expensesRes, debtPaymentsRes, repairPaymentsRes] = await Promise.all([
          fetchHeavyCached('sales', `${API_URL}/sales?all=true`),
          fetchHeavyCached('debts', `${API_URL}/customers/debts/all`),
          fetchHeavyCached('scrap', `${API_URL}/scrap-purchases`),
          fetchHeavyCached('exchanges', `${API_URL}/exchanges?all=true`),
          fetch(`${API_URL}/expenses?date=${endOfDayDate}`),
          fetch(`${API_URL}/debt-payments?date=${endOfDayDate}`),
          fetch(`${API_URL}/repair-payments?date=${endOfDayDate}`)
        ]);

        const expensesData = await expensesRes.json();
        let debtPayments = [];
        try {
          if (debtPaymentsRes.ok) {
            debtPayments = await debtPaymentsRes.json();
          }
        } catch (e) {
          console.error('Tahsilat verileri alınamadı:', e);
        }
        let repairPayments = [];
        try {
          if (repairPaymentsRes.ok) {
            repairPayments = await repairPaymentsRes.json();
          }
        } catch (e) {
          console.error('Tamirat ödeme verileri alınamadı:', e);
        }

        // Seçilen tarihe göre filtrele
        const filterByDate = (items, dateField = 'created_at') => {
          return (items || []).filter(item => {
            if (!item[dateField]) return false;
            const itemDate = new Date(item[dateField]);
            itemDate.setHours(0, 0, 0, 0);
            return itemDate.getTime() === selectedDate.getTime();
          });
        };

        const todaySales = filterByDate(Array.isArray(allSales) ? allSales : []);
        // Tahsilatlar: customer_debt_payments tablosundan al (tarih filtrelemesi backend'de yapıldı)
        const todayCollections = Array.isArray(debtPayments) ? debtPayments : [];
        const todayScrap = filterByDate(Array.isArray(allScrap) ? allScrap : []);
        const todayExchanges = filterByDate(Array.isArray(allExchanges) ? allExchanges : []);
        // Masraflar için tarih filtreleme
        const allExpenses = Array.isArray(expensesData) ? expensesData : (expensesData?.expenses || []);
        const todayExpenses = filterByDate(allExpenses);
        // Tamirat ödemeleri (tarih filtrelemesi backend'de yapıldı)
        const todayRepairPayments = Array.isArray(repairPayments) ? repairPayments : [];

        // Her mağaza için rapor oluştur
        const reports = {};

        stores.forEach(store => {
          const storeSales = todaySales.filter(s => s.store_id === store.id);
          const storeCollections = todayCollections.filter(c => c.store_id === store.id);
          const storeScrap = todayScrap.filter(s => s.store_id === store.id || (s.metal_type === (store.id === 1 ? 'gold' : 'silver') && !s.store_id));
          const storeExchanges = todayExchanges.filter(e => e.store_id === store.id);
          const storeExpenses = todayExpenses.filter(e => e.store_id === store.id);
          const storeRepairPayments = todayRepairPayments.filter(r => r.store_id === store.id);

          // Ödeme yöntemlerine göre grupla
          const cashSales = storeSales.filter(s => s.payment_method === 'Nakit');
          const cardSales = storeSales.filter(s => s.payment_method === 'Kredi Kartı');
          const transferSales = storeSales.filter(s => s.payment_method === 'Havale/EFT');
          const openAccountSales = storeSales.filter(s => s.payment_method && s.payment_method.startsWith('Açık Hesap'));
          const mixedSales = storeSales.filter(s => s.payment_method === 'Karışık' || s.payment_method === 'Karışık Ödeme');
          const sanalPosSales = storeSales.filter(s => (s.payment_method === 'Sanal Pos' || (typeof s.payment_method === 'string' && s.payment_method.startsWith('shopify_'))) && s.sale_type !== 'refund');
          const sanalPosRefundSales = storeSales.filter(s => s.payment_method === 'Sanal Pos' && s.sale_type === 'refund');
          const bakiyeRefundSales = storeSales.filter(s => s.payment_method === 'Bakiye' && s.sale_type === 'refund');
          const acikHesapSalesRefundSales = storeSales.filter(s => typeof s.payment_method === 'string' && s.payment_method.startsWith('Açık Hesap') && s.sale_type === 'refund');

          const calcTotal = (arr) => arr.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0);

          // Karışık ödemelerin detaylarını ödeme türlerine dağıt
          let mixedCashTotal = 0;
          let mixedCardTotal = 0;
          let mixedTransferTotal = 0;
          let mixedOpenAccountTotal = 0;

          mixedSales.forEach(sale => {
            let details = null;
            try {
              details = typeof sale.payment_details === 'string'
                ? JSON.parse(sale.payment_details)
                : sale.payment_details;
            } catch (e) {}

            if (Array.isArray(details) && details.length > 0) {
              details.forEach(d => {
                const amountTL = parseFloat(d.amount_tl || d.amountTL || d.amount) || 0;
                if (d.method === 'Nakit') {
                  mixedCashTotal += amountTL;
                } else if (d.method === 'Kredi Kartı') {
                  mixedCardTotal += amountTL;
                } else if (d.method === 'Havale/EFT') {
                  mixedTransferTotal += amountTL;
                } else if (d.method && d.method.startsWith('Açık Hesap')) {
                  mixedOpenAccountTotal += amountTL;
                }
              });
            }
          });

          // Hurda gramları hesapla
          const scrapGoldItems = storeScrap.filter(s => s.metal_type === 'gold');
          const scrapSilverItems = storeScrap.filter(s => s.metal_type === 'silver');
          const scrapGoldGram = scrapGoldItems.reduce((sum, s) => sum + (parseFloat(s.pure_weight) || 0), 0);
          const scrapSilverGram = scrapSilverItems.reduce((sum, s) => sum + (parseFloat(s.pure_weight) || 0), 0);
          const scrapTotal = storeScrap.reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0);
          // Hurda nakit ödeme (sadece nakit ödenen kısım kasadan düşer)
          const scrapCashTotal = storeScrap.reduce((sum, s) => sum + (parseFloat(s.paid_cash) || 0), 0);

          // Satışa bağlı hurdalarda müşteriye ödenen nakit (hurda tutarı > ürün değeri durumu)
          let saleLinkedScrapCashPayment = 0;
          const saleLinkedScrapBySaleId = {};
          storeScrap.filter(s => s.sale_id).forEach(s => {
            if (!saleLinkedScrapBySaleId[s.sale_id]) saleLinkedScrapBySaleId[s.sale_id] = 0;
            saleLinkedScrapBySaleId[s.sale_id] += parseFloat(s.total_price) || 0;
          });
          Object.entries(saleLinkedScrapBySaleId).forEach(([saleId, scrapTotal]) => {
            const sale = storeSales.find(s => s.id === parseInt(saleId));
            if (sale && sale.payment_method === 'Nakit İade') {
              // receipt_data'da customer_refund_amount varsa onu kullan (kesin tutar)
              let receiptRefund = 0;
              try {
                const rd = typeof sale.receipt_data === 'string' ? JSON.parse(sale.receipt_data) : sale.receipt_data;
                if (rd && rd.customer_refund_amount > 0) {
                  receiptRefund = rd.customer_refund_amount;
                }
              } catch (e) {}

              if (receiptRefund > 0) {
                saleLinkedScrapCashPayment += receiptRefund;
              } else {
                // Fallback: hurda toplam - ürün net değeri
                const netProductValue = (parseFloat(sale.subtotal) || 0) - (parseFloat(sale.discount) || 0);
                const cashToCustomer = scrapTotal - netProductValue;
                if (cashToCustomer > 0) {
                  saleLinkedScrapCashPayment += cashToCustomer;
                }
              }
            }
          });

          // Tahsilat toplamı (customer_debt_payments tablosundan)
          const collectionTotal = storeCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
          // Nakit tahsilatlar (kasaya eklenir)
          const cashCollections = storeCollections.filter(c => !c.payment_method || c.payment_method.startsWith('Nakit'));
          const cashCollectionTotal = cashCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
          // Havale/EFT tahsilatlar
          const transferCollections = storeCollections.filter(c => c.payment_method && c.payment_method.startsWith('Havale/EFT'));
          const transferCollectionTotal = transferCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
          // Kredi Kartı tahsilatlar
          const cardCollections = storeCollections.filter(c => c.payment_method && c.payment_method.startsWith('Kredi Kartı'));
          const cardCollectionTotal = cardCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

          // Değişim/İade
          const cashRefundExchanges = storeExchanges.filter(e => e.payment_method === 'Nakit İade');
          const cashRefundTotal = cashRefundExchanges.reduce((sum, e) => sum + Math.abs(parseFloat(e.difference) || 0), 0);

          // Masraflar
          const expenseTotal = storeExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
          const cashExpenseTotal = storeExpenses.filter(e => e.payment_method === 'Nakit').reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

          // Nakit iade (sales tablosundan) — eski 'Nakit İade' payment_method'lu kayıtlar
          const cashRefundSales = storeSales.filter(s => s.payment_method === 'Nakit İade');
          const cashRefundSalesTotal = calcTotal(cashRefundSales);

          // Karma ödemeli iadeler ("2 farklı ödeme" vb.) — payment_details içinden Nakit/Kart/Havale kırılımı
          // (Tek ödemeli Nakit iadeler zaten cashSales'a -tutar ile dahil olur; karma iadeler payment_method
          // hiçbir filtreye uymadığı için elle ayrıştırılması gerekir.)
          const mixedRefunds = storeSales.filter(s =>
            s.sale_type === 'refund' && typeof s.payment_method === 'string' && /\d+\s+farklı\s+ödeme/i.test(s.payment_method)
          );
          let mixedRefundCashTotal = 0, mixedRefundCardTotal = 0, mixedRefundTransferTotal = 0;
          mixedRefunds.forEach(sale => {
            let details = null;
            try {
              details = typeof sale.payment_details === 'string' ? JSON.parse(sale.payment_details) : sale.payment_details;
            } catch (e) {}
            if (!Array.isArray(details)) return;
            details.forEach(d => {
              const amountTL = parseFloat(d.amount_tl || d.amountTL || d.amount) || 0;
              if (d.method === 'Nakit') mixedRefundCashTotal += amountTL;
              else if (d.method === 'Kredi Kartı') mixedRefundCardTotal += amountTL;
              else if (d.method === 'Havale/EFT') mixedRefundTransferTotal += amountTL;
            });
          });

          // Tamirat gelirleri
          let repairRevenueTotal = 0, repairRevenueCash = 0, repairRevenueCard = 0, repairRevenueTransfer = 0;
          storeRepairPayments.forEach(repair => {
            const amount = parseFloat(repair.payment_amount) || 0;
            repairRevenueTotal += amount;
            let details = null;
            try {
              details = typeof repair.payment_details === 'string' ? JSON.parse(repair.payment_details) : repair.payment_details;
            } catch (e) {}
            if (Array.isArray(details) && details.length > 0) {
              details.forEach(d => {
                const amt = parseFloat(d.amount) || 0;
                if (d.method === 'Nakit') repairRevenueCash += amt;
                else if (d.method === 'Kredi Kartı') repairRevenueCard += amt;
                else if (d.method === 'Havale/EFT') repairRevenueTransfer += amt;
              });
            } else if (repair.payment_method === 'Nakit') {
              repairRevenueCash += amount;
            } else if (repair.payment_method === 'Kredi Kartı') {
              repairRevenueCard += amount;
            } else if (repair.payment_method === 'Havale/EFT') {
              repairRevenueTransfer += amount;
            }
          });

          reports[store.id] = {
            storeName: store.name,
            storeId: store.id,
            date: endOfDayDate,
            totalSales: storeSales.length,
            grandTotal: calcTotal(storeSales) + repairRevenueTotal,
            cash: {
              count: cashSales.length,
              total: calcTotal(cashSales) + cashCollectionTotal + mixedCashTotal + repairRevenueCash - mixedRefundCashTotal
            },
            card: {
              count: cardSales.length,
              total: calcTotal(cardSales) + cardCollectionTotal + mixedCardTotal + repairRevenueCard - mixedRefundCardTotal
            },
            transfer: {
              count: transferSales.length,
              total: calcTotal(transferSales) + transferCollectionTotal + mixedTransferTotal + repairRevenueTransfer - mixedRefundTransferTotal
            },
            openAccount: {
              count: openAccountSales.length,
              total: calcTotal(openAccountSales) + mixedOpenAccountTotal
            },
            mixed: {
              count: mixedSales.length,
              total: 0
            },
            sanalPos: eTicaretSummary(sanalPosSales, calcTotal),
            sanalPosRefund: {
              count: sanalPosRefundSales.length,
              total: Math.abs(calcTotal(sanalPosRefundSales))
            },
            bakiyeRefund: {
              count: bakiyeRefundSales.length,
              total: Math.abs(calcTotal(bakiyeRefundSales))
            },
            acikHesapSalesRefund: {
              count: acikHesapSalesRefundSales.length,
              total: Math.abs(calcTotal(acikHesapSalesRefundSales))
            },
            collections: {
              count: storeCollections.length,
              total: collectionTotal,
              cashTotal: cashCollectionTotal,
              transferTotal: transferCollectionTotal,
              cardTotal: cardCollectionTotal
            },
            scrap: {
              count: storeScrap.length,
              total: scrapTotal,
              cashTotal: scrapCashTotal,
              goldGram: scrapGoldGram,
              silverGram: scrapSilverGram
            },
            exchanges: {
              count: storeExchanges.length,
              cashRefundTotal: cashRefundTotal
            },
            expenses: {
              count: storeExpenses.length,
              total: expenseTotal,
              cashTotal: cashExpenseTotal
            },
            cashRefundSales: {
              count: cashRefundSales.length,
              total: cashRefundSalesTotal
            },
            mixedRefund: {
              count: mixedRefunds.length,
              cashTotal: mixedRefundCashTotal,
              cardTotal: mixedRefundCardTotal,
              transferTotal: mixedRefundTransferTotal
            },
            repairRevenue: {
              count: storeRepairPayments.length,
              total: repairRevenueTotal,
              cashTotal: repairRevenueCash,
              cardTotal: repairRevenueCard,
              transferTotal: repairRevenueTransfer,
              items: storeRepairPayments
            },
            expectedCash: calcTotal(cashSales) + mixedCashTotal - cashRefundSalesTotal + cashCollectionTotal - scrapCashTotal - saleLinkedScrapCashPayment - cashRefundTotal - cashExpenseTotal + repairRevenueCash - mixedRefundCashTotal
          };
        });

        // Toplam rapor (tüm mağazalar)
        const allCollectionsTotal = todayCollections.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
        const allCashCollections = todayCollections.filter(c => !c.payment_method || c.payment_method.startsWith('Nakit')).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
        const allCardCollections = todayCollections.filter(c => c.payment_method && c.payment_method.startsWith('Kredi Kartı')).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
        const allTransferCollections = todayCollections.filter(c => c.payment_method && c.payment_method.startsWith('Havale/EFT')).reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);

        // Tüm mağazalar için karışık ödeme dağılımı
        let allMixedCash = 0, allMixedCard = 0, allMixedTransfer = 0, allMixedOpenAccount = 0;
        const allMixedSales = todaySales.filter(s => s.payment_method === 'Karışık' || s.payment_method === 'Karışık Ödeme');
        allMixedSales.forEach(sale => {
          let details = null;
          try {
            details = typeof sale.payment_details === 'string' ? JSON.parse(sale.payment_details) : sale.payment_details;
          } catch (e) {}
          if (Array.isArray(details) && details.length > 0) {
            details.forEach(d => {
              const amountTL = parseFloat(d.amount_tl || d.amountTL || d.amount) || 0;
              if (d.method === 'Nakit') allMixedCash += amountTL;
              else if (d.method === 'Kredi Kartı') allMixedCard += amountTL;
              else if (d.method === 'Havale/EFT') allMixedTransfer += amountTL;
              else if (d.method && d.method.startsWith('Açık Hesap')) allMixedOpenAccount += amountTL;
            });
          }
        });

        // Tüm mağazalar için karma ödemeli iadelerin kırılımı ("2 farklı ödeme" vb.)
        let allMixedRefundCash = 0, allMixedRefundCard = 0, allMixedRefundTransfer = 0;
        const allMixedRefunds = todaySales.filter(s =>
          s.sale_type === 'refund' && typeof s.payment_method === 'string' && /\d+\s+farklı\s+ödeme/i.test(s.payment_method)
        );
        allMixedRefunds.forEach(sale => {
          let details = null;
          try {
            details = typeof sale.payment_details === 'string' ? JSON.parse(sale.payment_details) : sale.payment_details;
          } catch (e) {}
          if (!Array.isArray(details)) return;
          details.forEach(d => {
            const amountTL = parseFloat(d.amount_tl || d.amountTL || d.amount) || 0;
            if (d.method === 'Nakit') allMixedRefundCash += amountTL;
            else if (d.method === 'Kredi Kartı') allMixedRefundCard += amountTL;
            else if (d.method === 'Havale/EFT') allMixedRefundTransfer += amountTL;
          });
        });

        // Tüm mağazalar için tamirat geliri toplamları
        let allRepairRevenueTotal = 0, allRepairRevenueCash = 0, allRepairRevenueCard = 0, allRepairRevenueTransfer = 0;
        todayRepairPayments.forEach(repair => {
          const amount = parseFloat(repair.payment_amount) || 0;
          allRepairRevenueTotal += amount;
          let details = null;
          try {
            details = typeof repair.payment_details === 'string' ? JSON.parse(repair.payment_details) : repair.payment_details;
          } catch (e) {}
          if (Array.isArray(details) && details.length > 0) {
            details.forEach(d => {
              const amt = parseFloat(d.amount) || 0;
              if (d.method === 'Nakit') allRepairRevenueCash += amt;
              else if (d.method === 'Kredi Kartı') allRepairRevenueCard += amt;
              else if (d.method === 'Havale/EFT') allRepairRevenueTransfer += amt;
            });
          } else if (repair.payment_method === 'Nakit') {
            allRepairRevenueCash += amount;
          } else if (repair.payment_method === 'Kredi Kartı') {
            allRepairRevenueCard += amount;
          } else if (repair.payment_method === 'Havale/EFT') {
            allRepairRevenueTransfer += amount;
          }
        });

        reports['all'] = {
          storeName: 'Tüm Mağazalar',
          storeId: 'all',
          date: endOfDayDate,
          totalSales: todaySales.length,
          grandTotal: todaySales.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) + allRepairRevenueTotal,
          cash: {
            count: todaySales.filter(s => s.payment_method === 'Nakit').length,
            total: todaySales.filter(s => s.payment_method === 'Nakit').reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) + allCashCollections + allMixedCash + allRepairRevenueCash - allMixedRefundCash
          },
          card: {
            count: todaySales.filter(s => s.payment_method === 'Kredi Kartı').length,
            total: todaySales.filter(s => s.payment_method === 'Kredi Kartı').reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) + allCardCollections + allMixedCard + allRepairRevenueCard - allMixedRefundCard
          },
          transfer: {
            count: todaySales.filter(s => s.payment_method === 'Havale/EFT').length,
            total: todaySales.filter(s => s.payment_method === 'Havale/EFT').reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) + allTransferCollections + allMixedTransfer + allRepairRevenueTransfer - allMixedRefundTransfer
          },
          openAccount: {
            count: todaySales.filter(s => s.payment_method && s.payment_method.startsWith('Açık Hesap')).length,
            total: todaySales.filter(s => s.payment_method && s.payment_method.startsWith('Açık Hesap')).reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0) + allMixedOpenAccount
          },
          mixed: {
            count: allMixedSales.length,
            total: 0
          },
          sanalPos: eTicaretSummary(todaySales, (arr) => arr.reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)),
          sanalPosRefund: {
            count: todaySales.filter(s => s.payment_method === 'Sanal Pos' && s.sale_type === 'refund').length,
            total: Math.abs(todaySales.filter(s => s.payment_method === 'Sanal Pos' && s.sale_type === 'refund').reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0))
          },
          collections: {
            count: todayCollections.length,
            total: allCollectionsTotal,
            cashTotal: allCashCollections,
            transferTotal: allTransferCollections,
            cardTotal: allCardCollections
          },
          scrap: {
            count: todayScrap.length,
            total: todayScrap.reduce((sum, s) => sum + (parseFloat(s.total_amount) || 0), 0),
            cashTotal: todayScrap.reduce((sum, s) => sum + (parseFloat(s.paid_cash) || 0), 0),
            goldGram: todayScrap.filter(s => s.metal_type === 'gold').reduce((sum, s) => sum + (parseFloat(s.pure_weight) || 0), 0),
            silverGram: todayScrap.filter(s => s.metal_type === 'silver').reduce((sum, s) => sum + (parseFloat(s.pure_weight) || 0), 0)
          },
          exchanges: {
            count: todayExchanges.length,
            cashRefundTotal: todayExchanges.filter(e => e.payment_method === 'Nakit İade').reduce((sum, e) => sum + Math.abs(parseFloat(e.difference) || 0), 0)
          },
          expenses: {
            count: todayExpenses.length,
            total: todayExpenses.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0),
            cashTotal: todayExpenses.filter(e => e.payment_method === 'Nakit').reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
          },
          cashRefundSales: {
            count: todaySales.filter(s => s.payment_method === 'Nakit İade').length,
            total: todaySales.filter(s => s.payment_method === 'Nakit İade').reduce((sum, s) => sum + (parseFloat(s.total) || 0), 0)
          },
          repairRevenue: {
            count: todayRepairPayments.length,
            total: allRepairRevenueTotal,
            cashTotal: allRepairRevenueCash,
            cardTotal: allRepairRevenueCard,
            transferTotal: allRepairRevenueTransfer,
            items: todayRepairPayments
          },
          expectedCash: Object.values(reports).filter(r => r.storeId !== 'all').reduce((sum, r) => sum + (r.expectedCash || 0), 0)
        };

        setEndOfDayReports(reports);
      } catch (error) {
        console.error('Gün sonu raporu yükleme hatası:', error);
      } finally {
        setEndOfDayLoading(false);
      }
    };

    fetchEndOfDayReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, endOfDayDate, stores.length, fetchHeavyCached]);

  // Döneme göre satışları filtrele
  const getFilteredSales = () => {
    const now = new Date();
    let startDate, endDate = now;

    switch (selectedPeriod) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month': {
        const target = getMonthTarget();
        startDate = new Date(target.getFullYear(), target.getMonth(), 1);
        if (monthOffset !== 0) {
          endDate = new Date(target.getFullYear(), target.getMonth() + 1, 0, 23, 59, 59);
        }
        break;
      }
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'alltime':
        startDate = new Date(2020, 0, 1);
        break;
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          startDate = new Date(customDateRange.start);
          endDate = new Date(customDateRange.end);
          endDate.setHours(23, 59, 59);
        } else {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        }
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return sales.filter(s => {
      const saleDate = new Date(s.created_at);
      if (saleDate < startDate || saleDate > endDate) return false;

      // Mağaza filtresi
      if (reportStoreFilter !== 'all') {
        const storeId = Number(s.store_id);
        if (reportStoreFilter === 'gold') {
          if (storeId !== 1) return false; // Akik Jewellery (Altın)
        } else if (reportStoreFilter === 'silver') {
          if (storeId !== 2 && storeId !== 3) return false; // Gümüş mağazaları
        } else {
          if (storeId !== Number(reportStoreFilter)) return false; // Belirli mağaza
        }
      }

      return true;
    });
  };

  const filteredSales = getFilteredSales();

  // Değişim (exchange) bağlantı verileri - personel prim hesaplaması için
  // Değişim = iade + yeni satış. Orijinal satışın tutarı orijinal personelde kalmalı,
  // yeni satışın sadece farkı değişimi yapan personele yazılmalı.
  const exchangeLinkage = (() => {
    // Exchange kayıtları (exchanges tablosundan gelenler, exchange_id property'si var)
    const exchangeRecords = filteredSales.filter(s => s.exchange_id);

    // Değişim satışları (yeni satış olarak oluşturulanlar)
    const exchangeNewSales = filteredSales.filter(s =>
      s.record_type === 'sale' && s.notes && s.notes.includes('Değişim satışı')
    );

    // Map: exchange_id → exchange record
    const exchangeMap = {};
    exchangeRecords.forEach(exc => {
      exchangeMap[exc.exchange_id] = exc;
    });

    // Değişime bağlı exchange ID'leri (saf iade değil, değişim olanlar)
    const linkedExchangeIds = new Set();
    // Map: sale.id → { returnTotal, exchangeIds }
    const exchangeSaleInfo = {};

    exchangeNewSales.forEach(sale => {
      const match = sale.notes?.match(/İade Fiş ID:\s*([\d,\s]+)/);
      if (match) {
        const excIds = match[1].split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        let totalReturn = 0;
        excIds.forEach(excId => {
          if (exchangeMap[excId]) {
            totalReturn += Math.abs(Number(exchangeMap[excId].return_total) || 0);
            linkedExchangeIds.add(excId);
          }
        });
        exchangeSaleInfo[sale.id] = { returnTotal: totalReturn, exchangeIds: excIds };
      }
    });

    return { linkedExchangeIds, exchangeSaleInfo };
  })();

  // Bir kayıt "gerçek satış" olarak sayılmalı mı? (sayım/adet hesaplarında kullanılır)
  // Hariç: full refund kayıtları, exchange iade kayıtları, status='refunded' orijinaller.
  // Dahil: completed satışlar, exchange orijinalleri (status='completed' kalır), "Değişim satışı" yeni satışlar.
  // (Gelir hesaplamalarında bu filtre kullanılmaz; orada negatif iade kayıtları orijinalleri sıfırlar.)
  const isCountableOrder = (sale) => {
    if (sale.record_type === 'refund' || sale.record_type === 'exchange') return false;
    if (sale.sale_type === 'refund') return false;
    if (sale.status === 'refunded') return false;
    return true;
  };

  // Satışın gerçek ödenen tutarını hesapla (indirim ve hurda düşülmüş)
  const getSaleDisplayAmount = (sale) => {
    // Exchange/refund kayıtları için doğrudan total kullan
    if (sale.record_type === 'exchange' || sale.record_type === 'refund') {
      return Number(sale.total) || 0;
    }

    const total = Number(sale.total) || 0;
    const subtotal = Number(sale.subtotal) || 0;
    const discount = Number(sale.discount) || 0;
    const scrapAmount = Number(sale.scrap_total_amount) || 0;
    const scrapDebt = Number(sale.scrap_debt_discount) || 0;

    // total == subtotal ve indirim varsa, indirim total'e uygulanmamış demektir.
    // Tolerans 0.01 TL: 1 TL'lik gevşek tolerans küçük (örn. 0.50 TL) gerçek indirimleri
    // "uygulanmamış" sanıp indirimi ikinci kez düşürüyordu.
    if (discount > 0 && subtotal > 0 && Math.abs(total - subtotal) < 0.01) {
      return total - discount - scrapDebt;
    }
    return total + scrapAmount;
  };

  // İstatistikleri hesapla
  const calculateOverviewStats = () => {
    // Gelir: tüm kayıtlar (negatif iade kayıtları orijinalleri otomatik sıfırlar)
    const totalRevenue = filteredSales.reduce((sum, s) => sum + getSaleDisplayAmount(s), 0);
    // Sayım/indirim/ürün adedi: sadece gerçek satışlar (iade ve refunded orijinaller hariç)
    const countableSales = filteredSales.filter(isCountableOrder);
    const totalOrders = countableSales.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const totalDiscount = countableSales.reduce((sum, s) => sum + Number(s.discount || 0), 0);
    const netRevenue = totalRevenue - totalDiscount;
    const totalItems = countableSales.reduce((sum, s) => {
      if (s.items && Array.isArray(s.items)) {
        return sum + s.items.reduce((itemSum, item) => itemSum + (Number(item.quantity) || 1), 0);
      }
      return sum + 1;
    }, 0);

    // Önceki dönemle karşılaştırma
    const now = new Date();
    let prevStartDate, prevEndDate;

    if (selectedPeriod === 'today') {
      prevStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      prevEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (selectedPeriod === 'week') {
      prevStartDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      prevEndDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (selectedPeriod === 'month') {
      const target = getMonthTarget();
      prevStartDate = new Date(target.getFullYear(), target.getMonth() - 1, 1);
      prevEndDate = new Date(target.getFullYear(), target.getMonth(), 0);
    } else {
      prevStartDate = new Date(now.getFullYear() - 1, 0, 1);
      prevEndDate = new Date(now.getFullYear() - 1, 11, 31);
    }

    const prevSales = sales.filter(s => {
      const saleDate = new Date(s.created_at);
      return saleDate >= prevStartDate && saleDate < prevEndDate;
    });
    const prevRevenue = prevSales.reduce((sum, s) => sum + getSaleDisplayAmount(s), 0);
    const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100).toFixed(1) : 0;

    // Tamirat gelirini yalnızca gösterilen Toplam/Net Gelir'e ekle (Kasa Raporu ile tutarlı).
    // avgOrderValue, revenueChange ve ödeme %'leri satış-bazlı kalır (tamiratsız).
    const repairTotal = Number(repairRevenue?.total) || 0;

    return {
      totalRevenue: totalRevenue + repairTotal,
      totalOrders, avgOrderValue, totalDiscount,
      netRevenue: netRevenue + repairTotal,
      totalItems, revenueChange, prevRevenue
    };
  };

  // Mağaza bazlı istatistikler - TÜM mağazaları göster
  const calculateStoreStats = () => {
    const storeStats = {};

    // Önce tüm mağazaları ekle
    stores.forEach(store => {
      storeStats[store.id] = {
        storeId: store.id,
        storeName: store.name,
        totalRevenue: 0,
        orderCount: 0,
        avgOrder: 0
      };
    });

    // Satışları mağazalara ekle
    // Gelir: tüm kayıtlar (iade kayıtları orijinalleri sıfırlar)
    // Sipariş sayısı: sadece gerçek satışlar
    filteredSales.forEach(sale => {
      const storeId = sale.store_id;
      if (!storeId) return;
      if (!storeStats[storeId]) {
        storeStats[storeId] = {
          storeId,
          storeName: 'Bilinmeyen Mağaza',
          totalRevenue: 0,
          orderCount: 0,
          avgOrder: 0
        };
      }
      storeStats[storeId].totalRevenue += getSaleDisplayAmount(sale);
      if (isCountableOrder(sale)) {
        storeStats[storeId].orderCount += 1;
      }
    });

    // Tamirat gelirini ilgili mağazanın cirosuna ekle (Kasa Raporu ile tutarlı).
    // orderCount satış-bazlı kalır (tamirat "sipariş" sayılmaz).
    const repairByStore = repairRevenue?.byStore || {};
    Object.keys(repairByStore).forEach(sid => {
      if (!storeStats[sid]) return;
      storeStats[sid].totalRevenue += Number(repairByStore[sid]) || 0;
    });

    // Ortalama hesapla
    Object.values(storeStats).forEach(store => {
      store.avgOrder = store.orderCount > 0 ? store.totalRevenue / store.orderCount : 0;
    });

    return Object.values(storeStats).sort((a, b) => b.totalRevenue - a.totalRevenue);
  };

  // İsmi normalize et (trim + alfabetik sırala) - personel eşleştirmede kullanılır
  const normalizeStaffName = (name) => {
    if (!name) return '';
    return name.split(/[,&]/).map(n => n.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b, 'tr')).join(', ');
  };

  // Personel istatistikleri artık /api/employees/sales-performance endpoint'inden çekilir
  // (Personnel sayfası prim hesabı ile aynı mantık)

  // Ödeme yöntemi istatistikleri
  const calculatePaymentStats = () => {
    const paymentStats = {};
    let totalRevenue = 0;

    filteredSales.forEach(sale => {
      const method = sale.payment_method || 'Belirtilmemiş';
      if (!paymentStats[method]) {
        paymentStats[method] = { name: method, revenue: 0, count: 0 };
      }
      paymentStats[method].revenue += getSaleDisplayAmount(sale);
      // Sayı: sadece gerçek satışlar (refunded orijinaller ve iade kayıtları hariç)
      if (isCountableOrder(sale)) {
        paymentStats[method].count += 1;
      }
      totalRevenue += getSaleDisplayAmount(sale);
    });

    // Yüzdeleri hesapla
    Object.values(paymentStats).forEach(p => {
      p.percent = totalRevenue > 0 ? (p.revenue / totalRevenue * 100).toFixed(1) : 0;
    });

    return Object.values(paymentStats).sort((a, b) => b.revenue - a.revenue);
  };

  // Günlük satış grafiği verisi
  const getDailySalesData = () => {
    const dailyData = {};
    const now = new Date();

    // Son 30 günü hazırla
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const key = date.toISOString().split('T')[0];
      dailyData[key] = { date: key, revenue: 0, orders: 0 };
    }

    filteredSales.forEach(sale => {
      const key = new Date(sale.created_at).toISOString().split('T')[0];
      if (dailyData[key]) {
        dailyData[key].revenue += getSaleDisplayAmount(sale);
        if (isCountableOrder(sale)) {
          dailyData[key].orders += 1;
        }
      }
    });

    return Object.values(dailyData);
  };

  // Saatlik dağılım
  const getHourlyDistribution = () => {
    const hourlyData = Array(24).fill(null).map((_, i) => ({
      hour: i,
      label: `${i.toString().padStart(2, '0')}:00`,
      revenue: 0,
      orders: 0
    }));

    filteredSales.forEach(sale => {
      const hour = new Date(sale.created_at).getHours();
      hourlyData[hour].revenue += getSaleDisplayAmount(sale);
      if (isCountableOrder(sale)) {
        hourlyData[hour].orders += 1;
      }
    });

    return hourlyData;
  };

  const stats = calculateOverviewStats();
  const storeStats = calculateStoreStats();
  // Personel istatistikleri Personnel sayfasındaki prim hesabıyla aynı backend endpoint'inden gelir.
  // Tamirat geliri BURAYA dahil edilmez (Genel Bakış/Mağaza Raporları'ndan farklı olarak) —
  // Personel Performansı sadece satış cirosunu gösterir.
  const staffStats = staffPerformance.map(s => {
    const totalRevenue = Number(s.bonus_eligible_sales_total) || 0;
    const orderCount = Number(s.sales_count) || 0;
    return {
      staffId: s.employee_id,
      staffName: s.name,
      role: s.position || 'staff',
      totalRevenue,
      grossSales: Number(s.gross_sales_total) || 0,
      refundsTotal: Number(s.refunds_total) || 0,
      netSales: Number(s.net_sales_total) || 0,
      orderCount,
      avgOrder: orderCount > 0 ? totalRevenue / orderCount : 0,
      commission: Number(s.commission_amount) || 0,
      commissionRate: Number(s.commission_rate) || 0
    };
  }).sort((a, b) => {
    if (b.totalRevenue !== a.totalRevenue) return b.totalRevenue - a.totalRevenue;
    return a.staffName.localeCompare(b.staffName, 'tr');
  });
  const paymentStats = calculatePaymentStats();
  const dailySalesData = getDailySalesData();
  const hourlyData = getHourlyDistribution();

  // Maksimum değerleri bul (bar chart için)
  const maxDailyRevenue = Math.max(...dailySalesData.map(d => d.revenue), 1);
  const maxHourlyRevenue = Math.max(...hourlyData.map(d => d.revenue), 1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
  };

  // Seçili personelin satışlarını getir
  const getStaffSales = (staffName) => {
    return filteredSales.filter(sale => {
      // Değişime bağlı exchange iade kayıtlarını atla
      if (sale.exchange_id && exchangeLinkage.linkedExchangeIds.has(sale.exchange_id)) {
        return false;
      }
      return normalizeStaffName(sale.staff_name) === staffName;
    });
  };

  // Satışın personel bazlı görüntüleme tutarını hesapla (değişimlerde farkı gösterir)
  const getStaffDisplayAmount = (sale) => {
    let amount = getSaleDisplayAmount(sale);
    if (exchangeLinkage.exchangeSaleInfo[sale.id]) {
      amount = amount - exchangeLinkage.exchangeSaleInfo[sale.id].returnTotal;
    }
    return amount;
  };

  // Personel detay modalını aç
  const handleStaffClick = (staffData) => {
    setSelectedStaff(staffData);
    setShowStaffModal(true);
  };

  return (
    <div className={`reports-container ${reportMenuOpen ? 'report-menu-open' : ''}`}>
      {/* Sol Rapor Menüsü */}
      <div className={`report-side-menu ${reportMenuOpen ? 'open' : ''}`}>
        <div className="report-side-menu-header">
          <h3>Raporlar</h3>
          <button className="report-side-menu-close" onClick={() => setReportMenuOpen(false)}><X size={18} /></button>
        </div>
        <div className="report-side-menu-content">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`report-menu-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setReportMenuOpen(false); }}
            >
              <span className="report-menu-icon">{tab.icon}</span>
              <span>{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="report-main-content">
      <header className="page-header">
        <div className="header-left">
          <button className="report-hamburger-btn" onClick={() => setReportMenuOpen(!reportMenuOpen)}>
            <Menu size={20} />
          </button>
          <button className="back-btn" onClick={() => navigate('/')}><ArrowLeft size={18} /> Ana Sayfa</button>
          <h1>Raporlar & Analizler</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <button className="action-header-btn" onClick={() => { window.print(); if (localStorage.getItem('directPrintEnabled') === 'true') { setTimeout(() => window.close(), 500); } }}><Printer size={18} /> Raporu Yazdır</button>
      </header>

      {loading ? (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '100px 20px', gap: '28px'
        }}>
          <div style={{ position: 'relative', width: '120px', height: '120px' }}>
            <svg width="120" height="120" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="60" cy="60" r="52" fill="none" stroke="#e0f2f1" strokeWidth="8" />
              <circle cx="60" cy="60" r="52" fill="none" stroke="#00bfa6" strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - loadingProgress / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
              />
            </svg>
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', flexDirection: 'column'
            }}>
              <span style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                {loadingProgress}
              </span>
              <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500 }}>%</span>
            </div>
          </div>
          <p style={{
            fontSize: '15px', color: '#64748b', margin: 0, fontWeight: 500,
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            {loadingText}
          </p>
        </div>
      ) : (
      <div className="reports-content">
        {/* Aktif Tab Göstergesi */}
        <div className="report-active-tab-indicator">
          <span className="report-active-tab-icon">{tabs.find(t => t.id === activeTab)?.icon}</span>
          <span className="report-active-tab-name">{tabs.find(t => t.id === activeTab)?.name}</span>
        </div>

        {/* Period Selector */}
        <div className="period-section">
          <div className="period-selector">
            {periods.map(period => {
              if (period.id === 'month' && selectedPeriod === 'month') {
                const t = getMonthTarget();
                const nextDisabled = monthOffset >= 0;
                return (
                  <div key={period.id} style={{ display: 'inline-flex', alignItems: 'stretch', gap: '0' }}>
                    <button
                      className="period-btn active"
                      onClick={() => setMonthOffset(monthOffset - 1)}
                      title="Önceki ay"
                      style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, padding: '6px 8px' }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      className="period-btn active"
                      onClick={() => setMonthOffset(0)}
                      title={monthOffset !== 0 ? 'Bu aya dön' : 'Bu ay'}
                      style={{ borderRadius: 0, borderLeft: 'none', borderRight: 'none', minWidth: '120px' }}
                    >
                      {TR_MONTHS[t.getMonth()]} {t.getFullYear()}
                    </button>
                    <button
                      className="period-btn active"
                      onClick={() => !nextDisabled && setMonthOffset(monthOffset + 1)}
                      disabled={nextDisabled}
                      title={nextDisabled ? 'Gelecek ay' : 'Sonraki ay'}
                      style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '6px 8px', opacity: nextDisabled ? 0.4 : 1, cursor: nextDisabled ? 'not-allowed' : 'pointer' }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                );
              }
              return (
                <button
                  key={period.id}
                  className={`period-btn ${selectedPeriod === period.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedPeriod(period.id);
                    if (period.id !== 'month') setMonthOffset(0);
                  }}
                >
                  {period.name}
                </button>
              );
            })}
          </div>
          {selectedPeriod === 'custom' && (
            <div className="custom-date-range">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
              />
              <span>-</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
              />
            </div>
          )}
          <div className="store-filter-selector" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
            {[
              { id: 'all', name: 'Tümü' },
              { id: 'gold', name: 'Akik Gold' },
              { id: 'silver', name: 'Gümüş Mağazaları' },
              { id: '2', name: 'Akik Gümüş Osmangazi' },
              { id: '3', name: 'Akik Gümüş Yeşilsu' }
            ].map(store => (
              <button
                key={store.id}
                className={`period-btn ${reportStoreFilter === store.id ? 'active' : ''}`}
                onClick={() => setReportStoreFilter(store.id)}
              >
                {store.name}
              </button>
            ))}
          </div>
        </div>

        {/* GENEL BAKIŞ TAB */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            {/* Ana İstatistikler */}
            <div className="stats-grid">
              <div className="stat-card primary">
                <div className="stat-icon"><Coins size={20} /></div>
                <div className="stat-info">
                  <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
                  <div className="stat-label">Toplam Gelir (Tamirat Dahil)</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><ShoppingCart size={20} style={{ color: '#0f766e' }} /></div>
                <div className="stat-info">
                  <div className="stat-value">{stats.totalOrders}</div>
                  <div className="stat-label">Toplam Satış</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><Package size={20} style={{ color: '#0f766e' }} /></div>
                <div className="stat-info">
                  <div className="stat-value">{stats.totalItems}</div>
                  <div className="stat-label">Satılan Ürün</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><BarChart3 size={20} style={{ color: '#0f766e' }} /></div>
                <div className="stat-info">
                  <div className="stat-value">{formatCurrency(stats.avgOrderValue)}</div>
                  <div className="stat-label">Ortalama Sepet</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><Tag size={20} style={{ color: '#0f766e' }} /></div>
                <div className="stat-info">
                  <div className="stat-value">{formatCurrency(stats.totalDiscount)}</div>
                  <div className="stat-label">Toplam İndirim</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon"><TrendingUp size={20} style={{ color: '#10b981' }} /></div>
                <div className="stat-info">
                  <div className="stat-value">{formatCurrency(stats.netRevenue)}</div>
                  <div className="stat-label">Net Gelir (Tamirat Dahil)</div>
                </div>
              </div>
            </div>

            {/* Günlük Satış Grafiği */}
            <div className="chart-card">
              <h3>Günlük Satış Trendi</h3>
              <div className="bar-chart">
                {dailySalesData.slice(-14).map((day, index) => (
                  <div key={index} className="bar-item">
                    <div className="bar-container">
                      <div
                        className="bar"
                        style={{ height: `${(day.revenue / maxDailyRevenue) * 100}%` }}
                        title={`${formatDate(day.date)}: ${formatCurrency(day.revenue)}`}
                      ></div>
                    </div>
                    <span className="bar-label">{formatDate(day.date)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Saatlik Dağılım */}
            <div className="chart-card">
              <h3>Saatlik Satış Dağılımı</h3>
              <div className="bar-chart hourly">
                {hourlyData.filter((_, i) => i >= 9 && i <= 22).map((hour, index) => (
                  <div key={index} className="bar-item">
                    <div className="bar-container">
                      <div
                        className="bar"
                        style={{ height: `${(hour.revenue / maxHourlyRevenue) * 100}%` }}
                        title={`${hour.label}: ${formatCurrency(hour.revenue)} (${hour.orders} satış)`}
                      ></div>
                    </div>
                    <span className="bar-label">{hour.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Son Satışlar */}
            <div className="data-card">
              <h3>Son İşlemler</h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Tarih</th>
                      <th>Tür</th>
                      <th>Müşteri</th>
                      <th>Personel</th>
                      <th>Tutar</th>
                      <th>Ödeme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSales.slice(0, 15).map(sale => (
                      <tr key={sale.exchange_id ? `exc-${sale.exchange_id}` : sale.id} className={sale.record_type === 'refund' ? 'refund-row' : sale.record_type === 'exchange' ? 'exchange-row' : ''}>
                        <td>#{sale.receipt_number || sale.store_sale_number || sale.id}</td>
                        <td>{new Date(sale.created_at).toLocaleString('tr-TR')}</td>
                        <td>
                          <span className={`type-badge ${sale.record_type}`}>
                            {sale.record_type === 'sale' ? 'Satış' :
                             sale.record_type === 'refund' ? 'İade' :
                             sale.record_type === 'exchange' ? 'Değişim' : 'Satış'}
                          </span>
                        </td>
                        <td>{sale.customer_name || '-'}</td>
                        <td>{sale.staff_name || '-'}</td>
                        <td className={`amount ${sale.record_type === 'refund' ? 'refund-amount' : ''}`}>
                          {sale.record_type === 'refund' ? '-' : ''}{formatCurrency(Math.abs(getSaleDisplayAmount(sale)))}
                        </td>
                        <td><span className={`badge ${sale.payment_method?.toLowerCase().replace(/[^a-z]/g, '-')}`}>{sale.payment_method}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* MAĞAZA RAPORLARI TAB */}
        {activeTab === 'stores' && (
          <div className="tab-content">
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>
              Mağaza Cirosu (Tamirat Dahil)
            </div>
            <div className="stats-grid">
              {storeStats.map((store, index) => (
                <div key={store.storeId} className={`stat-card ${index === 0 ? 'primary' : ''}`}>
                  <div className="stat-icon"><Store size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} /></div>
                  <div className="stat-info">
                    <div className="stat-value">{formatCurrency(store.totalRevenue)}</div>
                    <div className="stat-label">{store.storeName}</div>
                  </div>
                </div>
              ))}
            </div>

            {storeStats.length === 0 && (
              <div className="empty-state">
                <BarChart3 size={48} style={{ color: '#9ca3af' }} />
                <p>Bu dönemde mağaza satışı bulunamadı</p>
              </div>
            )}

            {storeStats.length > 0 && (
              <div className="chart-card">
                <h3>Mağaza Karşılaştırması (Tamirat Dahil)</h3>
                <div className="horizontal-bars">
                  {storeStats.map((store, index) => {
                    const maxRevenue = storeStats[0]?.totalRevenue || 1;
                    return (
                      <div key={store.storeId} className="h-bar-item">
                        <div className="h-bar-label">{store.storeName}</div>
                        <div className="h-bar-container">
                          <div
                            className="h-bar"
                            style={{ width: `${(store.totalRevenue / maxRevenue) * 100}%` }}
                          ></div>
                          <span className="h-bar-value">{formatCurrency(store.totalRevenue)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* GÜN SONU RAPORLARI TAB */}
        {activeTab === 'endofday' && (
          <div className="tab-content">
            {/* Tarih ve Mağaza Seçimi */}
            <div className="eod-filters">
              <div className="eod-date-picker">
                <label>
                  <Calendar size={16} />
                  Tarih:
                </label>
                <input
                  type="date"
                  value={endOfDayDate}
                  onChange={(e) => setEndOfDayDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
                <button
                  className="btn-today"
                  onClick={() => setEndOfDayDate(new Date().toISOString().split('T')[0])}
                >
                  Bugün
                </button>
              </div>
              <div className="eod-store-filter">
                <label>
                  <Store size={16} />
                  Mağaza:
                </label>
                <select
                  value={selectedEodStore}
                  onChange={(e) => setSelectedEodStore(e.target.value)}
                >
                  <option value="all">Tüm Mağazalar</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn-refresh"
                onClick={() => setEndOfDayDate(endOfDayDate)}
                disabled={endOfDayLoading}
              >
                <RefreshCw size={16} className={endOfDayLoading ? 'spinning' : ''} />
                Yenile
              </button>
            </div>

            {endOfDayLoading ? (
              <Loading text="Rapor yükleniyor..." size="medium" />
            ) : (
              <>
                {/* Özet Kartlar */}
                {selectedEodStore === 'all' && (
                  <div className="eod-summary-cards">
                    {stores.map(store => {
                      const report = endOfDayReports[store.id];
                      if (!report) return null;
                      return (
                        <div
                          key={store.id}
                          className={`eod-store-card ${selectedEodStore === store.id.toString() ? 'active' : ''}`}
                          onClick={() => setSelectedEodStore(store.id.toString())}
                        >
                          <div className="eod-store-header">
                            <Store size={18} />
                            <span>{store.name}</span>
                          </div>
                          <div className="eod-store-stats">
                            <div className="eod-stat">
                              <span className="eod-stat-value">{formatCurrency(report.grandTotal)}</span>
                              <span className="eod-stat-label">Ciro</span>
                            </div>
                            <div className="eod-stat">
                              <span className="eod-stat-value">{report.totalSales}</span>
                              <span className="eod-stat-label">İşlem</span>
                            </div>
                            <div className="eod-stat highlight">
                              <span className="eod-stat-value">{formatCurrency(report.expectedCash)}</span>
                              <span className="eod-stat-label">Kasa</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Seçili Mağaza Detayı */}
                {endOfDayReports[selectedEodStore] && (
                  <div className="eod-detail-card">
                    <div className="eod-detail-header">
                      <h3>
                        <Store size={20} />
                        {endOfDayReports[selectedEodStore].storeName}
                      </h3>
                      <span className="eod-date">
                        <Clock size={16} />
                        {new Date(endOfDayDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>

                    <div className="eod-detail-grid">
                      {/* Satış Özeti */}
                      <div className="eod-section">
                        <h4>📊 Günlük Toplam</h4>
                        <div className="eod-row total">
                          <span>Toplam Ciro</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].grandTotal)}</span>
                        </div>
                        <div className="eod-row">
                          <span>Toplam İşlem</span>
                          <span>{endOfDayReports[selectedEodStore].totalSales} adet</span>
                        </div>
                        {endOfDayReports[selectedEodStore].collections.count > 0 && (
                          <div className="eod-row">
                            <span>Tahsilat ({endOfDayReports[selectedEodStore].collections.count} işlem)</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].collections.total)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].repairRevenue && endOfDayReports[selectedEodStore].repairRevenue.count > 0 && (
                          <div className="eod-row">
                            <span>Tamirat Geliri ({endOfDayReports[selectedEodStore].repairRevenue.count} adet)</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].repairRevenue.total)}</span>
                          </div>
                        )}
                      </div>

                      {/* Ödeme Dağılımı */}
                      <div className="eod-section">
                        <h4>💰 Ödeme Dağılımı</h4>
                        <div className="eod-row">
                          <span>Nakit</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].cash.total)}</span>
                        </div>
                        <div className="eod-row">
                          <span>Kredi Kartı</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].card.total)}</span>
                        </div>
                        <div className="eod-row">
                          <span>Havale/EFT</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].transfer.total)}</span>
                        </div>
                        {endOfDayReports[selectedEodStore].openAccount.total > 0 && (
                          <div className="eod-row">
                            <span>Veresiye</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].openAccount.total)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].sanalPos && endOfDayReports[selectedEodStore].sanalPos.total > 0 && (
                          <div className="eod-row">
                            <span>{eTicaretCardLabel(endOfDayReports[selectedEodStore].sanalPos)}</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].sanalPos.total)}</span>
                          </div>
                        )}
                        {(endOfDayReports[selectedEodStore].sanalPosRefund?.total || 0) > 0 && (
                          <div className="eod-row negative">
                            <span>Sanal Pos İade</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].sanalPosRefund.total)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].cashRefundSales.total > 0 && (
                          <div className="eod-row negative">
                            <span>Nakit İade</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].cashRefundSales.total)}</span>
                          </div>
                        )}
                      </div>

                      {/* Kasa Detayı */}
                      <div className="eod-section">
                        <h4>💵 Kasa (Nakit)</h4>
                        <div className="eod-row">
                          <span>Nakit Giriş (Satış + Tahsilat{endOfDayReports[selectedEodStore].repairRevenue && endOfDayReports[selectedEodStore].repairRevenue.cashTotal > 0 ? ' + Tamirat' : ''})</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].cash.total)}</span>
                        </div>
                        {endOfDayReports[selectedEodStore].cashRefundSales.total > 0 && (
                          <div className="eod-row negative">
                            <span>Nakit İade</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].cashRefundSales.total)}</span>
                          </div>
                        )}
                        {(endOfDayReports[selectedEodStore].sanalPosRefund?.total || 0) > 0 && (
                          <div className="eod-row negative">
                            <span>Sanal Pos İade</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].sanalPosRefund.total)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].scrap.cashTotal > 0 && (
                          <div className="eod-row negative">
                            <span>Hurda Nakit Ödeme</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].scrap.cashTotal)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].exchanges.cashRefundTotal > 0 && (
                          <div className="eod-row negative">
                            <span>Değişim Nakit İade</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].exchanges.cashRefundTotal)}</span>
                          </div>
                        )}
                        {endOfDayReports[selectedEodStore].expenses.cashTotal > 0 && (
                          <div className="eod-row negative">
                            <span>Nakit Masraf</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].expenses.cashTotal)}</span>
                          </div>
                        )}
                        <div className="eod-row highlight">
                          <span>Kasa Toplamı</span>
                          <span>{formatCurrency(endOfDayReports[selectedEodStore].expectedCash)}</span>
                        </div>
                      </div>

                      {/* Hurda Alımları */}
                      {endOfDayReports[selectedEodStore].scrap.count > 0 && (
                        <div className="eod-section">
                          <h4>⚙️ Hurda Alımları</h4>
                          <div className="eod-row">
                            <span>Hurda Toplam ({endOfDayReports[selectedEodStore].scrap.count} adet)</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].scrap.total)}</span>
                          </div>
                          {endOfDayReports[selectedEodStore].scrap.cashTotal > 0 && (
                            <div className="eod-row sub negative">
                              <span>└ Nakit Ödeme (Kasadan)</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].scrap.cashTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].scrap.goldGram > 0 && (
                            <div className="eod-row sub">
                              <span>└ Altın Hurda</span>
                              <span>{endOfDayReports[selectedEodStore].scrap.goldGram.toFixed(2)} gr saf</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].scrap.silverGram > 0 && (
                            <div className="eod-row sub">
                              <span>└ Gümüş Hurda</span>
                              <span>{endOfDayReports[selectedEodStore].scrap.silverGram.toFixed(2)} gr saf</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Değişim/İade */}
                      {endOfDayReports[selectedEodStore].exchanges.count > 0 && (
                        <div className="eod-section">
                          <h4>🔄 Değişim & İade</h4>
                          <div className="eod-row">
                            <span>Toplam İşlem</span>
                            <span>{endOfDayReports[selectedEodStore].exchanges.count} adet</span>
                          </div>
                          {endOfDayReports[selectedEodStore].exchanges.cashRefundTotal > 0 && (
                            <div className="eod-row negative">
                              <span>Nakit İade (Kasadan)</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].exchanges.cashRefundTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].exchanges.cardRefundTotal > 0 && (
                            <div className="eod-row negative">
                              <span>Kredi Kartı İade</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].exchanges.cardRefundTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].exchanges.havaleRefundTotal > 0 && (
                            <div className="eod-row negative">
                              <span>Havale İade</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].exchanges.havaleRefundTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].exchanges.voucherRefundTotal > 0 && (
                            <div className="eod-row negative">
                              <span>Açık Hesaba İade</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].exchanges.voucherRefundTotal)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Masraflar */}
                      {endOfDayReports[selectedEodStore].expenses.count > 0 && (
                        <div className="eod-section">
                          <h4>💸 Masraflar</h4>
                          <div className="eod-row negative">
                            <span>Masraf Toplamı ({endOfDayReports[selectedEodStore].expenses.count} adet)</span>
                            <span>-{formatCurrency(endOfDayReports[selectedEodStore].expenses.total)}</span>
                          </div>
                          {endOfDayReports[selectedEodStore].expenses.cashTotal > 0 && (
                            <div className="eod-row sub negative">
                              <span>└ Nakit Masraf (Kasadan)</span>
                              <span>-{formatCurrency(endOfDayReports[selectedEodStore].expenses.cashTotal)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tamirat Geliri */}
                      {endOfDayReports[selectedEodStore].repairRevenue && endOfDayReports[selectedEodStore].repairRevenue.count > 0 && (
                        <div className="eod-section">
                          <h4>🔧 Tamirat Geliri</h4>
                          <div className="eod-row">
                            <span>Tamirat Toplam ({endOfDayReports[selectedEodStore].repairRevenue.count} adet)</span>
                            <span>{formatCurrency(endOfDayReports[selectedEodStore].repairRevenue.total)}</span>
                          </div>
                          {endOfDayReports[selectedEodStore].repairRevenue.cashTotal > 0 && (
                            <div className="eod-row sub">
                              <span>└ Nakit (Kasaya)</span>
                              <span>{formatCurrency(endOfDayReports[selectedEodStore].repairRevenue.cashTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].repairRevenue.cardTotal > 0 && (
                            <div className="eod-row sub">
                              <span>└ Kredi Kartı</span>
                              <span>{formatCurrency(endOfDayReports[selectedEodStore].repairRevenue.cardTotal)}</span>
                            </div>
                          )}
                          {endOfDayReports[selectedEodStore].repairRevenue.transferTotal > 0 && (
                            <div className="eod-row sub">
                              <span>└ Havale/EFT</span>
                              <span>{formatCurrency(endOfDayReports[selectedEodStore].repairRevenue.transferTotal)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Tüm Mağazalar Karşılaştırma Tablosu */}
                {selectedEodStore === 'all' && Object.keys(endOfDayReports).length > 1 && (
                  <div className="eod-comparison-table">
                    <h3>Mağaza Karşılaştırması</h3>
                    <div className="table-wrapper">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Mağaza</th>
                            <th>Ciro</th>
                            <th>İşlem</th>
                            <th>Nakit</th>
                            <th>Kart</th>
                            <th>Transfer</th>
                            <th>Hurda</th>
                            <th>Masraf</th>
                            <th>Kasa</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stores.map(store => {
                            const report = endOfDayReports[store.id];
                            if (!report) return null;
                            return (
                              <tr key={store.id}>
                                <td className="store-name">{store.name}</td>
                                <td className="amount">{formatCurrency(report.grandTotal)}</td>
                                <td>{report.totalSales}</td>
                                <td>{formatCurrency(report.cash.total)}</td>
                                <td>{formatCurrency(report.card.total)}</td>
                                <td>{formatCurrency(report.transfer.total)}</td>
                                <td>{formatCurrency(report.scrap.total)}</td>
                                <td className="negative">-{formatCurrency(report.expenses.total)}</td>
                                <td className="highlight">{formatCurrency(report.expectedCash)}</td>
                              </tr>
                            );
                          })}
                          {/* Toplam Satır */}
                          <tr className="total-row">
                            <td><strong>TOPLAM</strong></td>
                            <td className="amount"><strong>{formatCurrency(endOfDayReports['all']?.grandTotal || 0)}</strong></td>
                            <td><strong>{endOfDayReports['all']?.totalSales || 0}</strong></td>
                            <td><strong>{formatCurrency(endOfDayReports['all']?.cash.total || 0)}</strong></td>
                            <td><strong>{formatCurrency(endOfDayReports['all']?.card.total || 0)}</strong></td>
                            <td><strong>{formatCurrency(endOfDayReports['all']?.transfer.total || 0)}</strong></td>
                            <td><strong>{formatCurrency(endOfDayReports['all']?.scrap.total || 0)}</strong></td>
                            <td className="negative"><strong>-{formatCurrency(endOfDayReports['all']?.expenses.total || 0)}</strong></td>
                            <td className="highlight"><strong>{formatCurrency(endOfDayReports['all']?.expectedCash || 0)}</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {Object.keys(endOfDayReports).length === 0 && stores.length === 0 && (
                  <div className="empty-state">
                    <RefreshCw size={48} style={{ color: '#9ca3af' }} />
                    <p>Mağaza bilgileri yükleniyor...</p>
                  </div>
                )}
                {Object.keys(endOfDayReports).length === 0 && stores.length > 0 && (
                  <div className="empty-state">
                    <Calendar size={48} style={{ color: '#9ca3af' }} />
                    <p>Bu tarihte kayıt bulunamadı</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* PERSONEL PERFORMANSI TAB */}
        {activeTab === 'staff' && (
          <div className="tab-content">
            <div className="data-card">
              <h3>
                Personel Satış Performansı ({staffStats.length} personel)
                {staffPerformanceLoading && <span style={{ marginLeft: 10, fontSize: 13, color: '#64748b' }}>Yükleniyor...</span>}
              </h3>
              <div className="table-wrapper">
                <table className="data-table performance-table">
                  <thead>
                    <tr>
                      <th>Sıra</th>
                      <th>Personel</th>
                      <th>Satış Adedi</th>
                      <th>Toplam Satış</th>
                      <th>Toplam İade</th>
                      <th>Net Satış</th>
                      <th>Ortalama Sepet</th>
                      <th>Prim Oranı</th>
                      <th>Prim Tutarı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffStats.map((s, index) => (
                      <tr key={s.staffId} className={`${s.orderCount > 0 && index < 3 ? 'top-performer' : ''} ${s.orderCount === 0 ? 'no-sales' : ''}`}>
                        <td>
                          <span className={`rank ${s.orderCount > 0 ? `rank-${index + 1}` : 'rank-none'}`}>
                            {s.orderCount > 0 ? (index === 0 ? <Trophy size={18} style={{ color: '#f59e0b' }} /> : index === 1 ? <Medal size={18} style={{ color: '#9ca3af' }} /> : index === 2 ? <Award size={18} style={{ color: '#cd7f32' }} /> : `#${index + 1}`) : '-'}
                          </span>
                        </td>
                        <td className="staff-name clickable" onClick={() => handleStaffClick(s)}>{s.staffName}</td>
                        <td className={s.orderCount === 0 ? 'zero-value' : ''}>{s.orderCount}</td>
                        <td className={`amount ${s.grossSales === 0 ? 'zero-value' : ''}`}>{formatCurrency(s.grossSales)}</td>
                        <td className={s.refundsTotal === 0 ? 'zero-value' : ''} style={s.refundsTotal > 0 ? { color: '#dc2626' } : undefined}>{s.refundsTotal > 0 ? `-${formatCurrency(s.refundsTotal)}` : formatCurrency(0)}</td>
                        <td className={`amount ${s.netSales === 0 ? 'zero-value' : ''}`}>{formatCurrency(s.netSales)}</td>
                        <td className={s.orderCount === 0 ? 'zero-value' : ''}>{formatCurrency(s.avgOrder)}</td>
                        <td className={s.commissionRate > 0 ? '' : 'zero-value'}>%{Number(s.commissionRate || 0).toFixed(2)}</td>
                        <td className={`commission ${s.commission === 0 ? 'zero-value' : ''}`}>{formatCurrency(s.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {staffStats.length === 0 && (
                <div className="empty-state">
                  <Users size={48} style={{ color: '#9ca3af' }} />
                  <p>Sistemde kayıtlı personel bulunamadı</p>
                </div>
              )}
            </div>

            {/* Özet İstatistikler */}
            {staffStats.length > 0 && (
              <div className="summary-cards">
                <div className="summary-card highlight">
                  <div className="summary-icon"><Coins size={24} style={{ color: '#f59e0b' }} /></div>
                  <div className="summary-info">
                    <div className="summary-value">{formatCurrency(staffStats.reduce((sum, s) => sum + s.commission, 0))}</div>
                    <div className="summary-label">Toplam Prim Ödemesi</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><Users size={24} style={{ color: '#3b82f6' }} /></div>
                  <div className="summary-info">
                    <div className="summary-value">{staffStats.filter(s => s.orderCount > 0).length} / {staffStats.length}</div>
                    <div className="summary-label">Satış Yapan Personel</div>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><BarChart3 size={24} style={{ color: '#8b5cf6' }} /></div>
                  <div className="summary-info">
                    <div className="summary-value">{formatCurrency(staffStats.reduce((sum, s) => sum + s.totalRevenue, 0) / (staffStats.filter(s => s.orderCount > 0).length || 1))}</div>
                    <div className="summary-label">Ortalama Personel Cirosu</div>
                  </div>
                </div>
              </div>
            )}

            {staffStats.filter(s => s.orderCount > 0).length > 0 && (
              <div className="chart-card">
                <h3>Personel Ciro Dağılımı</h3>
                <div className="horizontal-bars">
                  {staffStats.filter(s => s.orderCount > 0).slice(0, 10).map((s, index) => {
                    const maxRevenue = staffStats.filter(st => st.orderCount > 0)[0]?.totalRevenue || 1;
                    return (
                      <div key={s.staffId} className="h-bar-item">
                        <div className="h-bar-label">{s.staffName}</div>
                        <div className="h-bar-container">
                          <div
                            className={`h-bar color-${(index % 5) + 1}`}
                            style={{ width: `${(s.totalRevenue / maxRevenue) * 100}%` }}
                          ></div>
                          <span className="h-bar-value">{formatCurrency(s.totalRevenue)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {staffStats.length === 0 && (
              <div className="empty-state">
                <Users size={48} style={{ color: '#9ca3af' }} />
                <p>Sistemde kayıtlı personel bulunamadı</p>
              </div>
            )}
          </div>
        )}

        {/* KASİYER BAZLI DETAYLI RAPOR TAB */}
        {activeTab === 'staffSales' && (() => {
          // filteredSales'i kasiyer bazında grupla
          const groups = {};
          // Net (hurda öncesi satış değeri) = subtotal - discount + adjustment_total
          // sale.total hurda düşülmüş hâli olduğu için Net hesabı için uygun değil
          const getSaleNetValue = (sale) => {
            const subtotal = Number(sale.subtotal) || 0;
            const discount = Number(sale.discount) || 0;
            const adjTotal = Number(sale.adjustment_total) || 0;
            if (subtotal > 0) return subtotal - discount + adjTotal;
            return Number(sale.total) || 0;
          };

          // sale.staff_id zaman zaman bayat/eski bir id'ye (hatta employees.id'sine) işaret
          // edebiliyor, sale.staff_name de aynı kişi için bazen mağaza eki olmadan ("Celal
          // Tarık Basım") bazen ekli ("Celal Tarık Basım (Osmangazi)") kaydedilmiş olabiliyor.
          // Bu da aynı personelin raporda iki ayrı satıra bölünmesine yol açıyordu. Tekil
          // personelli satışlarda (isim virgül içermiyorsa) id üzerinden güncel/kanonik isme
          // çözüp tek satırda topluyoruz; çoklu personelli satışlarda metne dokunmuyoruz.
          //
          // Değişim/iade kayıtlarında staff_name işlemi yapan (yeni satışı yapan) operatörü
          // taşır — bilinçli olarak öyle: orijinal satış sahibi değişimden etkilenmemeli,
          // iade+değişim farkı yeni satışı/iadeyi işleyen personelin cirosundan düşülmeli.
          // sale.staff_id, employees.sales_staff_id VE employees.id gibi iki ayrı ID uzayına
          // düşebiliyor; bu iki uzay birbirinden bağımsız olduğundan bir personelin
          // sales_staff_id'si başka bir personelin employees.id'sine sayısal olarak denk
          // gelebiliyor (örn. Selçuk Kaya'nın sales_staff_id=20'si, Onur Tekin (Gümüş)'ün
          // employees.id=20'siyle çakışıyordu). Bu yüzden ID eşleşmesi kabul edilmeden önce
          // eşleşen personelin adı satırdaki gerçek staff_name ile uyumlu mu diye kontrol
          // edilir; uyuşmuyorsa (farklı bir kişiye ait tesadüfi ID çakışması) ID eşleşmesi
          // reddedilip satırın kendi staff_name'i kullanılır.
          const namesCompatible = (a, b) => {
            const na = String(a || '').toLowerCase().trim();
            const nb = String(b || '').toLowerCase().trim();
            if (!na || !nb) return false;
            return na === nb || na.includes(nb) || nb.includes(na);
          };
          const resolveStaffBucketName = (sale) => {
            const rawName = (sale.staff_name && String(sale.staff_name).trim()) || '';
            if (!rawName) return 'Belirtilmemiş';
            if (rawName.includes(',')) return rawName;
            const sid = sale.staff_id != null ? Number(sale.staff_id) : null;
            if (sid != null && !Number.isNaN(sid)) {
              const byEmpSalesStaffId = employeesForStaffMatch.find(e => Number(e.sales_staff_id) === sid);
              if (byEmpSalesStaffId && namesCompatible(byEmpSalesStaffId.name, rawName)) return byEmpSalesStaffId.name;
              const byEmpId = employeesForStaffMatch.find(e => Number(e.id) === sid);
              if (byEmpId && namesCompatible(byEmpId.name, rawName)) return byEmpId.name;
            }
            return rawName;
          };

          filteredSales.forEach(sale => {
            const name = resolveStaffBucketName(sale);
            if (!groups[name]) {
              groups[name] = { name, sales: [], totalSales: 0, totalDiscount: 0, totalRefund: 0 };
            }
            const g = groups[name];
            const netVal = getSaleNetValue(sale);
            const discount = Number(sale.discount) || 0;
            const isRefund = sale.record_type === 'refund' || sale.sale_type === 'refund';
            if (isRefund) {
              g.totalRefund += Math.abs(netVal);
            } else {
              g.totalSales += netVal;
              g.totalDiscount += discount;
            }
            g.sales.push(sale);
          });
          // Tekil (& içermeyen) ve çoklu kasiyerleri ayır; her grup içinde Net'e göre çoktan aza sırala
          const allGroups = Object.values(groups);
          const netOf = (g) => g.totalSales - g.totalRefund;
          const soloGroups = allGroups.filter(g => !g.name.includes('&')).sort((a, b) => netOf(b) - netOf(a));
          const sharedGroups = allGroups.filter(g => g.name.includes('&')).sort((a, b) => netOf(b) - netOf(a));
          const orderedGroups = [...soloGroups, ...sharedGroups];
          const grandTotalReceipts = orderedGroups.reduce((sum, g) => sum + g.sales.length, 0);

          const fmtTime = (d) => {
            try {
              return new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
            } catch (_) { return '-'; }
          };
          const fmtDate = (d) => {
            try {
              return new Date(d).toLocaleDateString('tr-TR');
            } catch (_) { return '-'; }
          };
          const typeLabel = (sale) => {
            const t = sale.record_type || (sale.sale_type === 'refund' ? 'refund' : 'sale');
            if (t === 'refund') return 'İade';
            if (t === 'exchange') return 'Değişim';
            return 'Satış';
          };

          // Ayar/milyem katsayıları (has gram hesabı için)
          const karatMultipliers = { '8': 333, '9': 375, '10': 417, '14': 585, '18': 750, '21': 875, '22': 916, '24-995': 995, '24-999': 999 };

          // Bir satış kalemi için yardımcılar
          const getItemBarcode = (it) => it.barcode || it.product_barcode || '-';
          const getItemAyar = (it) => {
            const metal = (it.metal_type || '').toLowerCase();
            if (metal === 'gold' && it.gold_karat) return `${it.gold_karat} Ayar`;
            if (metal === 'silver') return `${it.silver_purity || 925} Milyem`;
            return '-';
          };
          const getItemWeight = (it) => {
            const w = parseFloat(it.weight);
            return isNaN(w) || !w ? null : w;
          };
          const getItemHasGram = (it) => {
            const w = getItemWeight(it);
            if (!w) return null;
            const metal = (it.metal_type || '').toLowerCase();
            if (metal === 'gold' && it.gold_karat) {
              const m = karatMultipliers[String(it.gold_karat)];
              if (!m) return null;
              return (w * m) / 1000;
            }
            if (metal === 'silver') {
              const p = parseInt(it.silver_purity) || 925;
              return (w * p) / 1000;
            }
            return null;
          };
          const getItemRate = (it) => {
            const r = parseFloat(it.rate_used);
            return isNaN(r) || !r ? null : r;
          };
          const getItemQty = (it) => Math.max(1, parseInt(it.quantity) || 1);
          const getItemOriginalTotal = (it) => {
            const qty = getItemQty(it);
            const orig = parseFloat(it.original_unit_price ?? it.original_price);
            if (!isNaN(orig) && orig > 0) return orig * qty;
            // Fallback: discounted ise indirimsiz birim fiyatı total_price + discount_amount üzerinden bul
            const tp = parseFloat(it.total_price);
            const da = parseFloat(it.discount_amount) || 0;
            if (!isNaN(tp) && tp > 0) return tp + da;
            const up = parseFloat(it.discounted_unit_price ?? it.unit_price ?? it.price) || 0;
            return up * qty;
          };
          const fmtGram = (g) => (g == null ? '-' : `${Number(g).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} gr`);
          const fmtRate = (r) => (r == null ? '-' : `₺${Number(r).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

          // Her grup için indirimsiz toplamı (kalem orijinal fiyatları toplamı) hesapla
          // İade satırları normal fiyat toplamına dahil edilmez — iade tutarı sütunundan gözükür
          orderedGroups.forEach(g => {
            g.totalOriginal = g.sales.reduce((sum, sale) => {
              if (sale.record_type === 'refund' || sale.sale_type === 'refund') return sum;
              const items = Array.isArray(sale.items) ? sale.items : [];
              return sum + items.reduce((s, it) => s + (getItemOriginalTotal(it) || 0), 0);
            }, 0);
          });

          // Excel + PDF ortak payload (stil verilmiş util ile dışa aktarılır)
          const buildExportPayload = () => {
            const grandSales = orderedGroups.reduce((s, g) => s + g.totalSales, 0);
            const grandDiscount = orderedGroups.reduce((s, g) => s + g.totalDiscount, 0);
            const grandRefund = orderedGroups.reduce((s, g) => s + g.totalRefund, 0);
            const grandOriginal = orderedGroups.reduce((s, g) => s + (g.totalOriginal || 0), 0);
            const grandNet = grandSales - grandRefund;

            const columns = [
              'Fiş No', 'Tarih', 'Saat', 'Kasiyer', 'Tür', 'Açıklama',
              'Ürün', 'Barkod', 'Ayar', 'Gramaj', 'Has Gram', 'Kur (₺/gr)', 'Adet', 'Normal Fiyat',
              'İndirim', 'İade', 'Net'
            ];
            const colWidths = [
              { wch: 12 }, { wch: 11 }, { wch: 7 }, { wch: 20 }, { wch: 9 }, { wch: 26 },
              { wch: 24 }, { wch: 14 }, { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 11 }, { wch: 6 }, { wch: 15 },
              { wch: 13 }, { wch: 13 }, { wch: 13 }
            ];
            const numberCols = [9, 10, 11, 13, 14, 15, 16]; // gram, has, kur, indirimsizTop, indirim, iade, net
            const rows = [];

            orderedGroups.forEach(group => {
              const isShared = group.name.includes('&');
              rows.push({ kind: 'group', cells: [`${group.name}${isShared ? ' (Ortak)' : ''} — ${group.sales.length} fiş`], span: columns.length });
              const sortedSales = [...group.sales].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              sortedSales.forEach(sale => {
                const isRefund = sale.record_type === 'refund' || sale.sale_type === 'refund';
                const netVal = getSaleNetValue(sale);
                const discount = Number(sale.discount) || 0;
                const description = sale.notes || sale.customer_name || '';
                const items = Array.isArray(sale.items) ? sale.items : [];
                const fisHead = [
                  `#${sale.receipt_number || sale.store_sale_number || sale.id}`,
                  fmtDate(sale.created_at),
                  fmtTime(sale.created_at),
                  sale.staff_name || '',
                  typeLabel(sale),
                  description
                ];
                const fisTail = [
                  discount || null,
                  isRefund ? Math.abs(netVal) : null,
                  isRefund ? -Math.abs(netVal) : netVal
                ];
                if (items.length === 0) {
                  rows.push([...fisHead, '', '', '', null, null, null, null, null, ...fisTail]);
                } else {
                  items.forEach((it, idx) => {
                    const w = getItemWeight(it);
                    const hg = getItemHasGram(it);
                    const rate = getItemRate(it);
                    const qty = getItemQty(it);
                    const origTotal = getItemOriginalTotal(it);
                    const itemCols = [
                      it.product_name || it.name || '',
                      getItemBarcode(it) === '-' ? '' : getItemBarcode(it),
                      getItemAyar(it) === '-' ? '' : getItemAyar(it),
                      w == null ? null : Number(w),
                      hg == null ? null : Number(hg.toFixed(3)),
                      rate == null ? null : Number(rate),
                      qty,
                      isRefund ? null : (Number(origTotal) || 0)
                    ];
                    if (idx === 0) {
                      rows.push([...fisHead, ...itemCols, ...fisTail]);
                    } else {
                      // Sonraki kalem satırları: fiş başlığını boş bırak ve dip alanları da
                      rows.push(['', '', '', '', '', '', ...itemCols, '', '', '']);
                    }
                  });
                }
              });
              const groupNet = group.totalSales - group.totalRefund;
              // Dip toplam: ilk 13 sütun span'li label, sonra İndirimsiz/İndirim/İade/Net
              rows.push({
                kind: 'subtotal',
                span: 13,
                cells: [`${group.name} Dip Toplam`, group.totalOriginal || '', group.totalDiscount || '', group.totalRefund || '', groupNet]
              });
              rows.push([]); // ayraç
            });

            rows.push({
              kind: 'total',
              span: 13,
              cells: [`GENEL TOPLAM (Tüm Kasiyerler) — ${grandTotalReceipts} fiş`, grandOriginal || '', grandDiscount || '', grandRefund || '', grandNet]
            });

            return {
              title: 'Plasiyer Raporu',
              periodLabel: getPeriodLabel(),
              storeLabel: getStoreLabel(),
              columns,
              rows,
              colWidths,
              numberCols,
              sheetName: 'Kasiyer Detayli Rapor'
            };
          };

          const exportToExcel = () => exportReportToExcel(buildExportPayload());

          return (
            <div className="tab-content">
              <div className="data-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <h3 style={{ margin: 0 }}>
                    Plasiyer Raporu ({orderedGroups.length} kasiyer / {grandTotalReceipts} fiş)
                  </h3>
                  {orderedGroups.length > 0 && (
                    <button
                      onClick={exportToExcel}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                      title="Excel dosyası olarak indir"
                    >
                      <Download size={16} /> Excel İndir
                    </button>
                  )}
                </div>
                {orderedGroups.length === 0 ? (
                  <div className="empty-state">
                    <Receipt size={48} style={{ color: '#9ca3af' }} />
                    <p>Seçili dönemde kayıt bulunamadı</p>
                  </div>
                ) : (
                  <div className="table-wrapper" style={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto', position: 'relative' }}>
                    <table className="data-table">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', boxShadow: '0 1px 0 #e2e8f0' }}>
                        <tr>
                          <th style={{ width: '36px' }}></th>
                          <th>Fiş No</th>
                          <th>Fiş Tarihi</th>
                          <th>Fiş Saati</th>
                          <th>Kasiyer</th>
                          <th>Fiş Türü</th>
                          <th>Fiş Açıklaması</th>
                          <th>Barkod</th>
                          <th>Ayar</th>
                          <th style={{ textAlign: 'right' }}>Gramaj</th>
                          <th style={{ textAlign: 'right' }}>Has Gram</th>
                          <th style={{ textAlign: 'right' }}>Kur (₺/gr)</th>
                          <th style={{ textAlign: 'right' }}>Adet</th>
                          <th style={{ textAlign: 'right' }}>Normal Fiyat</th>
                          <th>İndirim Tutarı</th>
                          <th>İade Tutarı</th>
                          <th>Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderedGroups.map(group => {
                          const isShared = group.name.includes('&');
                          const sortedSales = [...group.sales].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                          return (
                            <React.Fragment key={group.name}>
                              <tr>
                                <td colSpan={17} style={{ padding: '10px 14px', background: isShared ? '#fef3c7' : '#f1f5f9', fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', borderTop: '2px solid #e2e8f0' }}>
                                  <span>
                                    <Users size={15} style={{ verticalAlign: 'middle', marginRight: '6px', color: isShared ? '#d97706' : '#0f766e' }} />
                                    {group.name} {isShared && <span style={{ fontSize: '0.72rem', color: '#92400e', fontWeight: 600, marginLeft: '6px' }}>(Ortak)</span>}
                                    <span style={{ marginLeft: '10px', color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>{group.sales.length} fiş</span>
                                  </span>
                                </td>
                              </tr>
                              {sortedSales.map(sale => {
                                const isRefund = sale.record_type === 'refund' || sale.sale_type === 'refund';
                                const total = getSaleNetValue(sale);
                                const discount = Number(sale.discount) || 0;
                                const description = sale.notes || sale.customer_name || '-';
                                const rowKey = sale.exchange_id ? `exc-${sale.exchange_id}` : `sale-${sale.id}`;
                                const items = Array.isArray(sale.items) ? sale.items : [];
                                const itemCount = items.length;
                                const hasMultipleItems = itemCount > 1;
                                const isExpanded = expandedStaffSaleIds.has(rowKey);
                                const singleItem = itemCount === 1 ? items[0] : null;

                                // Tek ürünlü satış için doğrudan kalem değerleri
                                const single = singleItem ? {
                                  barcode: getItemBarcode(singleItem),
                                  ayar: getItemAyar(singleItem),
                                  weight: getItemWeight(singleItem),
                                  hasGram: getItemHasGram(singleItem),
                                  rate: getItemRate(singleItem),
                                  qty: getItemQty(singleItem),
                                  origTotal: getItemOriginalTotal(singleItem)
                                } : null;

                                // Çok kalemli satışta fiş bazlı toplam adet ve indirimsiz toplam
                                const multiQty = hasMultipleItems ? items.reduce((s, it) => s + getItemQty(it), 0) : 0;
                                const multiOrigTotal = hasMultipleItems ? items.reduce((s, it) => s + getItemOriginalTotal(it), 0) : 0;

                                return (
                                  <React.Fragment key={rowKey}>
                                    <tr
                                      className={sale.record_type === 'refund' ? 'refund-row' : sale.record_type === 'exchange' ? 'exchange-row' : ''}
                                      onClick={hasMultipleItems ? () => toggleStaffSaleExpand(rowKey) : undefined}
                                      style={hasMultipleItems ? { cursor: 'pointer' } : undefined}
                                      title={hasMultipleItems ? (isExpanded ? 'Ürünleri gizle' : 'Ürünleri göster') : undefined}
                                    >
                                      <td style={{ width: '36px', textAlign: 'center' }}>
                                        {hasMultipleItems ? (
                                          <span
                                            aria-hidden="true"
                                            style={{ display: 'inline-flex', alignItems: 'center', color: '#475569' }}
                                          >
                                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                          </span>
                                        ) : (
                                          <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>—</span>
                                        )}
                                      </td>
                                      <td>#{sale.receipt_number || sale.store_sale_number || sale.id}</td>
                                      <td>{fmtDate(sale.created_at)}</td>
                                      <td>{fmtTime(sale.created_at)}</td>
                                      <td>{sale.staff_name || '-'}</td>
                                      <td>
                                        <span className={`type-badge ${sale.record_type || 'sale'}`}>{typeLabel(sale)}</span>
                                      </td>
                                      <td style={{ maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={description}>
                                        {description}
                                      </td>
                                      {/* Ürün detay sütunları */}
                                      {single ? (
                                        <>
                                          <td style={{ fontFamily: 'monospace', color: '#475569' }} title={singleItem.product_name || ''}>{single.barcode}</td>
                                          <td style={{ color: '#475569' }}>{single.ayar}</td>
                                          <td style={{ textAlign: 'right', color: '#475569' }}>{single.weight == null ? '-' : fmtGram(single.weight)}</td>
                                          <td style={{ textAlign: 'right', color: '#0f766e', fontWeight: 600 }}>{fmtGram(single.hasGram)}</td>
                                          <td style={{ textAlign: 'right', color: '#475569' }}>{fmtRate(single.rate)}</td>
                                          <td style={{ textAlign: 'right', color: '#475569' }}>{single.qty}</td>
                                          <td style={{ textAlign: 'right', fontWeight: 600, color: isRefund ? '#cbd5e1' : '#1e293b' }}>{isRefund ? '-' : formatCurrency(single.origTotal)}</td>
                                        </>
                                      ) : hasMultipleItems ? (
                                        <>
                                          <td style={{ color: '#94a3b8' }}>-</td>
                                          <td style={{ color: '#94a3b8' }}>-</td>
                                          <td style={{ color: '#94a3b8', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#94a3b8', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#94a3b8', textAlign: 'right' }}>-</td>
                                          <td style={{ textAlign: 'right', color: '#475569' }}>{multiQty}</td>
                                          <td style={{ textAlign: 'right', fontWeight: 600, color: isRefund ? '#cbd5e1' : '#1e293b' }}>{isRefund ? '-' : formatCurrency(multiOrigTotal)}</td>
                                        </>
                                      ) : (
                                        <>
                                          <td style={{ color: '#cbd5e1' }}>-</td>
                                          <td style={{ color: '#cbd5e1' }}>-</td>
                                          <td style={{ color: '#cbd5e1', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#cbd5e1', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#cbd5e1', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#cbd5e1', textAlign: 'right' }}>-</td>
                                          <td style={{ color: '#cbd5e1', textAlign: 'right' }}>-</td>
                                        </>
                                      )}
                                      <td style={{ color: discount > 0 ? '#7c3aed' : '#94a3b8' }}>{discount > 0 ? formatCurrency(discount) : '-'}</td>
                                      <td style={{ color: isRefund ? '#dc2626' : '#94a3b8' }}>{isRefund ? formatCurrency(Math.abs(total)) : '-'}</td>
                                      <td style={{ fontWeight: 600, color: isRefund ? '#dc2626' : '#0f766e' }}>{isRefund ? `-${formatCurrency(Math.abs(total))}` : formatCurrency(total)}</td>
                                    </tr>
                                    {isExpanded && hasMultipleItems && items.map((it, idx) => {
                                      const w = getItemWeight(it);
                                      const hg = getItemHasGram(it);
                                      const rate = getItemRate(it);
                                      const qty = getItemQty(it);
                                      const origTotal = getItemOriginalTotal(it);
                                      const subBg = '#f8fafc';
                                      const isLast = idx === items.length - 1;
                                      const baseStyle = { background: subBg, borderBottom: isLast ? '1px solid #e2e8f0' : 'none' };
                                      return (
                                        <tr key={`${rowKey}-sub-${idx}`}>
                                          <td style={baseStyle}></td>
                                          <td colSpan={6} style={{ ...baseStyle, paddingLeft: '32px', color: '#64748b', fontSize: '0.82rem' }}>
                                            <span style={{ color: '#94a3b8', marginRight: '6px' }}>↳</span>
                                            {it.product_name || it.name || `Ürün ${idx + 1}`}
                                          </td>
                                          <td style={{ ...baseStyle, fontFamily: 'monospace', color: '#475569', fontSize: '0.85rem' }}>{getItemBarcode(it)}</td>
                                          <td style={{ ...baseStyle, color: '#475569', fontSize: '0.85rem' }}>{getItemAyar(it)}</td>
                                          <td style={{ ...baseStyle, textAlign: 'right', color: '#475569', fontSize: '0.85rem' }}>{w == null ? '-' : fmtGram(w)}</td>
                                          <td style={{ ...baseStyle, textAlign: 'right', color: '#0f766e', fontWeight: 600, fontSize: '0.85rem' }}>{fmtGram(hg)}</td>
                                          <td style={{ ...baseStyle, textAlign: 'right', color: '#475569', fontSize: '0.85rem' }}>{fmtRate(rate)}</td>
                                          <td style={{ ...baseStyle, textAlign: 'right', color: '#475569', fontSize: '0.85rem' }}>{qty}</td>
                                          <td style={{ ...baseStyle, textAlign: 'right', fontWeight: 600, color: isRefund ? '#cbd5e1' : '#1e293b', fontSize: '0.85rem' }}>{isRefund ? '-' : formatCurrency(origTotal)}</td>
                                          <td style={baseStyle}></td>
                                          <td style={baseStyle}></td>
                                          <td style={baseStyle}></td>
                                        </tr>
                                      );
                                    })}
                                  </React.Fragment>
                                );
                              })}
                              {/* Kasiyer dip toplam satırı */}
                              {(() => {
                                const groupNet = group.totalSales - group.totalRefund;
                                const cellBg = isShared ? '#fef3c7' : '#f8fafc';
                                return (
                                  <tr>
                                    <td colSpan={13} style={{ padding: '8px 14px', background: cellBg, fontWeight: 700, fontSize: '0.82rem', color: '#475569', textAlign: 'right', borderTop: '1px dashed #cbd5e1' }}>
                                      {group.name} Dip Toplam
                                    </td>
                                    <td style={{ background: cellBg, fontWeight: 700, color: '#1e293b', textAlign: 'right', borderTop: '1px dashed #cbd5e1' }}>{group.totalOriginal > 0 ? formatCurrency(group.totalOriginal) : '-'}</td>
                                    <td style={{ background: cellBg, fontWeight: 700, color: group.totalDiscount > 0 ? '#7c3aed' : '#94a3b8', borderTop: '1px dashed #cbd5e1' }}>{group.totalDiscount > 0 ? formatCurrency(group.totalDiscount) : '-'}</td>
                                    <td style={{ background: cellBg, fontWeight: 700, color: group.totalRefund > 0 ? '#dc2626' : '#94a3b8', borderTop: '1px dashed #cbd5e1' }}>{group.totalRefund > 0 ? formatCurrency(group.totalRefund) : '-'}</td>
                                    <td style={{ background: cellBg, fontWeight: 700, color: groupNet < 0 ? '#dc2626' : '#0f766e', borderTop: '1px dashed #cbd5e1' }}>{formatCurrency(groupNet)}</td>
                                  </tr>
                                );
                              })()}
                            </React.Fragment>
                          );
                        })}
                        {/* Genel (mağaza) dip toplam satırı */}
                        {(() => {
                          const grandSales = orderedGroups.reduce((s, g) => s + g.totalSales, 0);
                          const grandDiscount = orderedGroups.reduce((s, g) => s + g.totalDiscount, 0);
                          const grandRefund = orderedGroups.reduce((s, g) => s + g.totalRefund, 0);
                          const grandOriginal = orderedGroups.reduce((s, g) => s + (g.totalOriginal || 0), 0);
                          const grandNet = grandSales - grandRefund;
                          return (
                            <tr>
                              <td colSpan={13} style={{ padding: '12px 14px', background: '#0f766e', color: '#fff', fontWeight: 800, fontSize: '0.9rem', textAlign: 'right', borderTop: '2px solid #064e44' }}>
                                GENEL TOPLAM (Tüm Kasiyerler) — {grandTotalReceipts} fiş
                              </td>
                              <td style={{ background: '#0f766e', color: '#fff', fontWeight: 800, fontSize: '0.9rem', textAlign: 'right', borderTop: '2px solid #064e44' }}>{grandOriginal > 0 ? formatCurrency(grandOriginal) : '-'}</td>
                              <td style={{ background: '#0f766e', color: '#fde68a', fontWeight: 800, fontSize: '0.9rem', borderTop: '2px solid #064e44' }}>{grandDiscount > 0 ? formatCurrency(grandDiscount) : '-'}</td>
                              <td style={{ background: '#0f766e', color: '#fecaca', fontWeight: 800, fontSize: '0.9rem', borderTop: '2px solid #064e44' }}>{grandRefund > 0 ? formatCurrency(grandRefund) : '-'}</td>
                              <td style={{ background: '#0f766e', color: '#fff', fontWeight: 800, fontSize: '0.9rem', borderTop: '2px solid #064e44' }}>{formatCurrency(grandNet)}</td>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Tamirat Servisi Ücretleri — plasiyerler bittikten sonra ayrı özet olarak gösterilir */}
                {(repairRevenue?.total || 0) > 0 && (
                  <div style={{ marginTop: '16px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <div style={{ padding: '10px 14px', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>
                      🔧 Tamirat Servisi Ücretleri
                    </div>
                    <div style={{ padding: '12px 14px', background: '#faf5ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                        Seçili dönemde teslim edilen tamiratların ücreti + alınan tamirat ödemeleri (plasiyer bazında değil, mağaza/dönem toplamıdır)
                      </span>
                      <span style={{ fontSize: '1rem', fontWeight: 800, color: '#6d28d9' }}>{formatCurrency(repairRevenue.total)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* KASA RAPORU TAB */}
        {activeTab === 'cashReport' && (() => {
          const r = cashReportData;
          const fmt = (n) => `₺${(Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const fmtN = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const storeName = getStoreLabel();

          const ledger = buildCashLedger(r);

          const buildCashReportPayload = () => {
            if (!r || !ledger) return null;
            const sections = [];

            // (+) GELİRLER — Kasa Raporu Özet ile birebir
            const gelirRows = ledger.gelir.map(([label, v]) => [label, v]);
            gelirRows.push({ kind: 'total', span: 1, cells: ['TOPLAM GELİR (+)', ledger.gelirTop] });
            sections.push({
              heading: '(+) GELİRLER',
              columns: ['İşlem Adı', 'Tutar (₺)'],
              rows: gelirRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });

            // (-) GİDERLER
            const giderRows = ledger.gider.map(([label, v]) => [label, -Math.abs(v)]);
            giderRows.push({ kind: 'total', span: 1, cells: ['TOPLAM GİDER (-)', -ledger.giderTop] });
            sections.push({
              heading: '(-) GİDERLER',
              columns: ['İşlem Adı', 'Tutar (₺)'],
              rows: giderRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });

            // GENEL TOPLAM & ALT DETAYLAR
            const altRows = [];
            altRows.push({ kind: 'total', span: 1, cells: ['KASA GENEL TOPLAM', ledger.kasaGenel] });
            if (Number(r.expectedCash) !== 0) altRows.push(['NAKİT KASA TOPLAM', Number(r.expectedCash)]);
            if (ledger.netKrediKarti !== 0) altRows.push(['NET KREDİ KARTI', ledger.netKrediKarti]);
            if (ledger.netHavaleSahis !== 0) altRows.push(['NET HAVALE ŞAHIS', ledger.netHavaleSahis]);
            if (ledger.netHavaleSirket !== 0) altRows.push(['NET HAVALE ŞİRKET', ledger.netHavaleSirket]);
            if (ledger.netHavaleETicaret !== 0) altRows.push(['NET HAVALE E-TİCARET', ledger.netHavaleETicaret]);
            eTicaretNetRows(r.sanalPos).forEach((row) => altRows.push(row));
            if (ledger.toplamHurda !== 0) altRows.push(['TOPLAM HURDA TUTARI', ledger.toplamHurda]);
            if (Number(r.openAccount?.total) !== 0) altRows.push(['AÇIK HESAP SATIŞLAR', Number(r.openAccount.total)]);
            if ((Number(r.musteriBakiyesiSales?.total) || 0) !== 0) altRows.push(['MÜŞTERİ BAKİYESİ SATIŞLAR', Number(r.musteriBakiyesiSales?.total || 0)]);
            if ((Number(r.exchanges?.voucherRefundTotal) || 0) + (Number(r.acikHesapSalesRefund?.total) || 0) + (Number(r.bakiyeRefund?.total) || 0) !== 0) altRows.push(['AÇIK HESABA İADELER', Number(r.exchanges?.voucherRefundTotal || 0) + Number(r.acikHesapSalesRefund?.total || 0) + Number(r.bakiyeRefund?.total || 0)]);
            if (Number(r.totalDiscount) !== 0) altRows.push(['İNDİRİMLER TOPLAM', Number(r.totalDiscount)]);
            sections.push({
              heading: 'Genel Toplam & Alt Detaylar',
              columns: ['Kalem', 'Tutar (₺)'],
              rows: altRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });

            // KASİYER LİSTESİ
            if (Array.isArray(r.cashierList) && r.cashierList.length > 0) {
              const sorted = [...r.cashierList].sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));
              const total = sorted.reduce((s, c) => s + (Number(c.total) || 0), 0);
              const totalSales = sorted.reduce((s, c) => s + (Number(c.salesCount) || 0), 0);
              sections.push({
                heading: 'Kasiyer Raporu',
                columns: ['Kasiyer', 'Satış', 'Nakit', 'Kart', 'Havale', 'Toplam'],
                rows: [
                  ...sorted.map(c => [c.name, Number(c.salesCount) || 0, Number(c.cash) || 0, Number(c.card) || 0, Number(c.transfer) || 0, Number(c.total) || 0]),
                  { kind: 'total', span: 1, cells: ['GENEL TOPLAM', totalSales, '', '', '', total] }
                ],
                numberCols: [2, 3, 4, 5],
                colWidths: [{ wch: 26 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 16 }]
              });
            }

            // HURDA ALIMLARI
            if (r.scrap && Array.isArray(r.scrap.items) && r.scrap.items.length > 0) {
              const total = Number(r.scrap.netTotal ?? r.scrap.total) || 0;
              sections.push({
                heading: 'Hurda Alımları',
                columns: ['Müşteri', 'Metal', 'Ayar', 'Brüt (gr)', 'Has (gr)', 'Net (gr)', 'Alış Kuru', 'Ödeme', 'Tutar'],
                rows: [
                  ...r.scrap.items.map(s => [
                    s.customer_name || 'Müşteri',
                    s.metal_type === 'gold' ? 'Altın' : 'Gümüş',
                    s.purity || '-',
                    Number(parseFloat(s.gross_weight) || 0),
                    Number(parseFloat(s.pure_weight) || 0),
                    Number(((parseFloat(s.gross_weight) || 0) - (parseFloat(s.waste_gram) || 0))),
                    Number(s.unit_price) || 0,
                    s.payment_method === 'Havale' && s.bank_account_name ? s.bank_account_name : (s.payment_method || 'Nakit'),
                    -Math.abs(Number(s.total_amount) || 0)
                  ]),
                  { kind: 'total', span: 8, cells: [`TOPLAM (${r.scrap.count} adet)`, -Math.abs(total)] }
                ],
                numberCols: [3, 4, 5, 6, 8],
                colWidths: [{ wch: 22 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }]
              });
            }

            // TAHSİLATLAR
            if (r.collections && Array.isArray(r.collections.items) && r.collections.items.length > 0) {
              sections.push({
                heading: 'Tahsilatlar',
                columns: ['Müşteri', 'Ödeme Yöntemi', 'Tutar'],
                rows: [
                  ...r.collections.items.map(c => [c.customer_name || '-', c.payment_method || 'Nakit', Number(c.amount) || 0]),
                  { kind: 'total', span: 2, cells: [`TOPLAM (${r.collections.count} adet)`, Number(r.collections.total) || 0] }
                ],
                numberCols: [2],
                colWidths: [{ wch: 26 }, { wch: 20 }, { wch: 16 }]
              });
            }

            return {
              title: 'Kasa Raporu',
              periodLabel: r.dateRange ? `${r.dateRange.start} → ${r.dateRange.end} (${r.dateRange.dayCount} gün)` : getPeriodLabel(),
              storeLabel: storeName,
              sections
            };
          };

          const exportCashExcel = () => { const p = buildCashReportPayload(); if (p) exportSectionedReportToExcel(p); };

          return (
            <div className="tab-content">
              {/* Eylem Çubuğu — dönem ve mağaza filtresi üstteki global seçiciden gelir */}
              {r && (
                <div className="data-card" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <button onClick={exportCashExcel} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }} title="Excel indir">
                      <Download size={14} /> Excel
                    </button>
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>
                      {storeName} · {r.dateRange?.dayCount} gün ({r.dateRange?.start} → {r.dateRange?.end})
                    </span>
                  </div>
                </div>
              )}

              {!r ? (
                <div className="data-card"><div className="empty-state"><Wallet size={48} style={{ color: '#9ca3af' }} /><p>{cashReportLoading ? 'Yükleniyor...' : 'Veri bulunamadı'}</p></div></div>
              ) : (
                <>
                  {/* Genel Özet Kartları — değerler "Kasa Raporu Özet" ile birebir */}
                  <div className="eod-summary-cards">
                    <div className="eod-summary-card">
                      <div className="card-icon teal"><Banknote size={20} /></div>
                      <div className="card-value" style={{ color: ledger.kasaGenel < 0 ? '#dc2626' : undefined }}>{fmt(ledger.kasaGenel)}</div>
                      <div className="card-label">Kasa Genel Toplam</div>
                    </div>
                    <div className="eod-summary-card">
                      <div className="card-icon blue"><ShoppingCart size={20} /></div>
                      <div className="card-value">{(Number(r.totalSales) || 0) + (Number(r.exchanges?.count) || 0)}</div>
                      <div className="card-label">İşlem Sayısı</div>
                    </div>
                    <div className="eod-summary-card">
                      <div className="card-icon green"><Banknote size={20} /></div>
                      <div className="card-value" style={{ color: (Number(r.expectedCash) || 0) < 0 ? '#dc2626' : undefined }}>{fmt(r.expectedCash)}</div>
                      <div className="card-label">Nakit Kasa Toplamı</div>
                    </div>
                    {ledger.netKrediKarti !== 0 && (
                      <div className="eod-summary-card">
                        <div className="card-icon purple"><CreditCard size={20} /></div>
                        <div className="card-value" style={{ color: ledger.netKrediKarti < 0 ? '#dc2626' : undefined }}>{fmt(ledger.netKrediKarti)}</div>
                        <div className="card-label">Net Kredi Kartı</div>
                      </div>
                    )}
                    {ledger.netHavaleToplam !== 0 && (
                      <div className="eod-summary-card" style={{ minHeight: 'auto' }}>
                        <div className="card-icon orange"><Send size={20} /></div>
                        <div className="card-value" style={ledger.netHavaleToplam < 0 ? { color: '#ef4444' } : {}}>
                          {ledger.netHavaleToplam < 0 ? '-' : ''}₺{Math.abs(ledger.netHavaleToplam).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="card-label">Net Havale/EFT</div>
                      </div>
                    )}
                    {(Number(r.openAccount?.total) || 0) > 0 && (
                      <div className="eod-summary-card">
                        <div className="card-icon red"><Coins size={20} /></div>
                        <div className="card-value">{fmt(r.openAccount.total)}</div>
                        <div className="card-label">Açık Hesap ({r.openAccount.count})</div>
                      </div>
                    )}
                    {(Number(r.sanalPos?.total) || 0) > 0 && (
                      <div className="eod-summary-card">
                        <div className="card-icon cyan"><CreditCard size={20} /></div>
                        <div className="card-value">{fmt(r.sanalPos.total)}</div>
                        <div className="card-label">{eTicaretCardLabel(r.sanalPos)} ({r.sanalPos.count})</div>
                      </div>
                    )}
                    {(Number(r.expenses?.total) || 0) > 0 && (
                      <div className="eod-summary-card">
                        <div className="card-icon" style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}><AlertTriangle size={20} /></div>
                        <div className="card-value" style={{ color: '#dc2626' }}>{fmt(r.expenses.total)}</div>
                        <div className="card-label">Toplam Masraf ({r.expenses.count})</div>
                      </div>
                    )}
                    {(Number(r.scrap?.count) || 0) > 0 && (
                      <div className="eod-summary-card">
                        <div className="card-icon" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}><Scale size={20} /></div>
                        <div className="card-value">{r.scrap.count} Adet</div>
                        <div className="card-label" style={{ fontSize: '11px', color: '#92400e' }}>
                          {((r.scrap.goldGrossGram || 0) + (r.scrap.silverGrossGram || 0)).toFixed(2)}gr brüt / {((r.scrap.goldNetGram ?? r.scrap.goldGrossGram ?? 0) + (r.scrap.silverNetGram ?? r.scrap.silverGrossGram ?? 0)).toFixed(2)}gr net
                        </div>
                        <div className="card-label">Toplam Hurda Alımı</div>
                      </div>
                    )}
                  </div>

                  {/* Ödeme Dağılımı (Gün Sonu Raporu birebir tasarım) */}
                  <div className="eod-two-col">
                    <div className="eod-col">
                      <div className="eod-col-panel">
                        <div className="eod-col-panel-header">
                          <Wallet size={16} /> Ödeme Dağılımı
                        </div>
                        <div className="eod-col-panel-body">
                          <div className="eod-payments" style={{ marginBottom: 0 }}>
                            {/* (+) GELİRLER — Kasa Raporu Özet ile birebir */}
                            <div className="eod-section-title" style={{ margin: '0', padding: '10px 18px 6px', borderBottom: 'none', color: '#059669' }}>
                              <Banknote size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />(+) Gelirler
                            </div>
                            {ledger.gelir.map(([label, v], i) => (
                              <div key={`g-${i}`} className="eod-payment-item" style={{ padding: '7px 18px' }}>
                                <span className="eod-payment-label">{label}</span>
                                <span className="eod-payment-value" style={{ color: '#059669' }}>₺{fmtN(v)}</span>
                              </div>
                            ))}
                            <div className="eod-payment-item" style={{ padding: '8px 18px', borderTop: '1px solid #e2e8f0' }}>
                              <span className="eod-payment-label" style={{ fontWeight: 700 }}>Toplam Gelir (+)</span>
                              <span className="eod-payment-value" style={{ color: '#059669', fontWeight: 700 }}>₺{fmtN(ledger.gelirTop)}</span>
                            </div>

                            {/* (-) GİDERLER */}
                            <div className="eod-section-title" style={{ margin: '0', padding: '10px 18px 6px', borderBottom: 'none', color: '#dc2626' }}>
                              <CreditCard size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />(-) Giderler
                            </div>
                            {ledger.gider.length === 0 ? (
                              <div className="eod-payment-item" style={{ padding: '7px 18px' }}>
                                <span className="eod-payment-label" style={{ color: '#94a3b8' }}>Gider bulunmuyor</span>
                                <span className="eod-payment-value" style={{ color: '#94a3b8' }}>₺0,00</span>
                              </div>
                            ) : ledger.gider.map(([label, v], i) => (
                              <div key={`gd-${i}`} className="eod-payment-item" style={{ padding: '7px 18px' }}>
                                <span className="eod-payment-label">{label}</span>
                                <span className="eod-payment-value" style={{ color: '#ef4444' }}>-₺{fmtN(v)}</span>
                              </div>
                            ))}
                            <div className="eod-payment-item" style={{ padding: '8px 18px', borderTop: '1px solid #e2e8f0' }}>
                              <span className="eod-payment-label" style={{ fontWeight: 700 }}>Toplam Gider (-)</span>
                              <span className="eod-payment-value" style={{ color: '#ef4444', fontWeight: 700 }}>-₺{fmtN(ledger.giderTop)}</span>
                            </div>

                            {/* KASA GENEL TOPLAM */}
                            <div className="eod-payment-item" style={{ padding: '10px 18px', background: '#f1f5f9', borderTop: '2px solid #cbd5e1' }}>
                              <span className="eod-payment-label" style={{ fontWeight: 800 }}>KASA GENEL TOPLAM</span>
                              <span className="eod-payment-value" style={{ fontWeight: 800, color: ledger.kasaGenel >= 0 ? '#059669' : '#ef4444' }}>₺{fmtN(ledger.kasaGenel)} {ledger.kasaGenel >= 0 ? '(+)' : '(-)'}</span>
                            </div>

                            {/* Net Dağılımlar (Alt Detaylar) */}
                            <div className="eod-section-title" style={{ margin: '0', padding: '10px 18px 6px', borderBottom: 'none' }}>
                              <Wallet size={14} style={{ marginRight: '5px', verticalAlign: 'middle' }} />Net Dağılımlar
                            </div>
                            {(Number(r.expectedCash) || 0) !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Nakit Kasa Toplam</span><span className="eod-payment-value">₺{fmtN(r.expectedCash)}</span></div>
                            )}
                            {ledger.netKrediKarti !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Net Kredi Kartı</span><span className="eod-payment-value">₺{fmtN(ledger.netKrediKarti)}</span></div>
                            )}
                            {ledger.netHavaleSahis !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Net Havale Şahıs</span><span className="eod-payment-value">₺{fmtN(ledger.netHavaleSahis)}</span></div>
                            )}
                            {ledger.netHavaleSirket !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Net Havale Şirket</span><span className="eod-payment-value">₺{fmtN(ledger.netHavaleSirket)}</span></div>
                            )}
                            {ledger.netHavaleETicaret !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Net Havale E-Ticaret</span><span className="eod-payment-value">₺{fmtN(ledger.netHavaleETicaret)}</span></div>
                            )}
                            {eTicaretNetRows(r.sanalPos).map(([label, amount]) => (
                              <div key={label} className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">{label.replace('NET E-TİCARET', 'Net E-Ticaret')}</span><span className="eod-payment-value">₺{fmtN(amount)}</span></div>
                            ))}
                            {ledger.toplamHurda !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Toplam Hurda Tutarı</span><span className="eod-payment-value">₺{fmtN(ledger.toplamHurda)}</span></div>
                            )}
                            {(Number(r.openAccount?.total) || 0) !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Açık Hesap Satışlar</span><span className="eod-payment-value">₺{fmtN(r.openAccount.total)}</span></div>
                            )}
                            {(Number(r.musteriBakiyesiSales?.total) || 0) !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Müşteri Bakiyesi Satışlar</span><span className="eod-payment-value">₺{fmtN(r.musteriBakiyesiSales?.total)}</span></div>
                            )}
                            {((Number(r.exchanges?.voucherRefundTotal) || 0) + (Number(r.acikHesapSalesRefund?.total) || 0) + (Number(r.bakiyeRefund?.total) || 0)) !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">Açık Hesaba İadeler</span><span className="eod-payment-value">₺{fmtN(Number(r.exchanges?.voucherRefundTotal || 0) + Number(r.acikHesapSalesRefund?.total || 0) + Number(r.bakiyeRefund?.total || 0))}</span></div>
                            )}
                            {(Number(r.totalDiscount) || 0) !== 0 && (
                              <div className="eod-payment-item sub-item" style={{ padding: '6px 18px' }}><span className="eod-payment-label">İndirimler Toplam</span><span className="eod-payment-value">₺{fmtN(r.totalDiscount)}</span></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* SAĞ KOLON: Kasiyer Raporu */}
                    <div className="eod-col">
                      {r.topCashier && (
                        <div className="eod-cashier-section" style={{ marginBottom: '14px' }}>
                          <div className="eod-cashier-header">
                            <div className="cashier-left">
                              <div className="cashier-icon" style={{ marginRight: '8px', width: '42px', height: '42px', background: '#0f766e', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Medal size={20} /></div>
                              <div className="cashier-meta">
                                <div className="cashier-name">{r.topCashier.name}</div>
                                <div className="cashier-stats">{r.topCashier.salesCount} satış{[(r.topCashier.cash || 0) > 0 && ` | N:₺${(r.topCashier.cash || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, (r.topCashier.card || 0) > 0 && ` K:₺${(r.topCashier.card || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, (r.topCashier.transfer || 0) > 0 && ` H:₺${(r.topCashier.transfer || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`].filter(Boolean).join('')}</div>
                              </div>
                            </div>
                            <div className="cashier-right">
                              <span className="cashier-total">₺{fmtN(r.topCashier.total)}</span>
                            </div>
                          </div>
                        </div>
                      )}
                      {Array.isArray(r.cashierList) && r.cashierList.filter(c => !r.topCashier || c.name !== r.topCashier.name).map((cashier, idx) => (
                        <div key={idx} className="eod-cashier-section">
                          <div className="eod-cashier-header">
                            <div className="cashier-left">
                              <div className="cashier-avatar">{cashier.name.charAt(0).toUpperCase()}</div>
                              <div className="cashier-meta">
                                <div className="cashier-name">{cashier.name}</div>
                                <div className="cashier-stats">{cashier.salesCount} satış{[(cashier.cash || 0) > 0 && ` | N:₺${(cashier.cash || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, (cashier.card || 0) > 0 && ` K:₺${(cashier.card || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, (cashier.transfer || 0) > 0 && ` H:₺${(cashier.transfer || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`].filter(Boolean).join('')}</div>
                              </div>
                            </div>
                            <div className="cashier-right">
                              <span className="cashier-total">₺{fmtN(cashier.total)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!r.cashierList || r.cashierList.length === 0) && (
                        <div className="eod-col-panel">
                          <div className="eod-col-panel-header"><Users size={16} /> Kasiyer Raporu</div>
                          <div className="eod-col-panel-body">
                            <div className="eod-payment-item" style={{ padding: '10px 18px', color: '#94a3b8' }}>
                              <span className="eod-payment-label">Bu dönemde kasiyer aktivitesi yok</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hurda Alımları Tablosu */}
                  {r.scrap && r.scrap.count > 0 && (
                    <>
                      <div className="eod-section-title"><Scale size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Hurda Alımları ({r.scrap.count})</div>
                      <div className="eod-products-table-wrapper" style={{ maxHeight: '300px' }}>
                        <table className="eod-products-table">
                          <thead>
                            <tr>
                              <th>Müşteri</th>
                              <th>Metal</th>
                              <th>Ayar</th>
                              <th>Brüt (gr)</th>
                              <th>Has (gr)</th>
                              <th>Net (gr)</th>
                              <th>Alış Kuru</th>
                              <th>Ödeme</th>
                              <th>Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(r.scrap.items || []).map(scrap => (
                              <tr key={scrap.id}>
                                <td style={{ fontWeight: 500 }}>{scrap.customer_name || 'Müşteri'}</td>
                                <td>{scrap.metal_type === 'gold' ? 'Altın' : 'Gümüş'}</td>
                                <td>{scrap.purity || '-'}</td>
                                <td>{parseFloat(scrap.gross_weight || 0).toFixed(2)}</td>
                                <td>{parseFloat(scrap.pure_weight || 0).toFixed(2)}</td>
                                <td>{((parseFloat(scrap.gross_weight) || 0) - (parseFloat(scrap.waste_gram) || 0)).toFixed(2)}</td>
                                <td>₺{fmtN(scrap.unit_price)}</td>
                                <td style={{ color: '#64748b', fontSize: '0.82rem' }}>{scrap.payment_method === 'Havale' && scrap.bank_account_name ? scrap.bank_account_name : (scrap.payment_method || 'Nakit')}</td>
                                <td style={{ fontWeight: 600, color: '#ef4444' }}>-₺{fmtN(scrap.total_amount)}</td>
                              </tr>
                            ))}
                            <tr className="eod-cashier-total-row">
                              <td colSpan={8} style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>Toplam:</td>
                              <td style={{ fontWeight: 700, color: '#ef4444', fontSize: '0.85rem' }}>-₺{fmtN(r.scrap.netTotal ?? r.scrap.total)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* Tahsilatlar Tablosu */}
                  {r.collections && r.collections.count > 0 && (
                    <>
                      <div className="eod-section-title"><Coins size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Tahsilatlar ({r.collections.count})</div>
                      <div className="eod-products-table-wrapper" style={{ maxHeight: '300px' }}>
                        <table className="eod-products-table">
                          <thead>
                            <tr>
                              <th>Müşteri</th>
                              <th>Ödeme Yöntemi</th>
                              <th>Tutar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(r.collections.items || []).map(col => (
                              <tr key={col.id}>
                                <td style={{ fontWeight: 500 }}>{col.customer_name || '-'}</td>
                                <td>{col.payment_method || 'Nakit'}</td>
                                <td style={{ fontWeight: 600, color: '#16a34a' }}>+₺{fmtN(col.amount)}</td>
                              </tr>
                            ))}
                            <tr className="eod-cashier-total-row">
                              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>Toplam:</td>
                              <td style={{ fontWeight: 700, color: '#16a34a', fontSize: '0.85rem' }}>+₺{fmtN(r.collections.total)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          );
        })()}

        {/* KASA RAPORU ÖZET TAB - Aylık Kasa Defteri Raporu Detaylı */}
        {activeTab === 'cashReportSummary' && (() => {
          const r = cashReportData;
          const fmtN = (n) => (Number(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          const storeName = getStoreLabel();

          // Kasa defteri özet için export payload
          const buildCashSummaryPayload = () => {
            if (!r) return null;
            const sections = [];

            const gelirRows = [];
            const giderRows = [];

            const gelirEntries = [
              ['Nakit Ödeme', r.cash?.salesTotal != null ? (Number(r.cash.salesTotal) || 0) : ((Number(r.cash?.total) || 0) - (Number(r.cash?.collectionTotal) || 0) - (Number(r.cash?.repairTotal) || 0))],
              ['Nakit Tahsilat', Number(r.cash?.collectionTotal) || 0],
              ['Nakit Tamirat', Number(r.cash?.repairTotal) || 0],
              ...eTicaretGelirRows(r.sanalPos),
              ['Kredi Kartı', Number(r.card?.salesTotal ?? r.card?.total) || 0],
              ['Kredi Kartı Tahsilat', Number(r.card?.collectionTotal) || 0],
              ['Kredi Kartı Tamirat', Number(r.card?.repairTotal) || 0]
            ];
            gelirEntries.filter(([, v]) => v > 0).forEach(([label, v]) => gelirRows.push([label, v]));

            const tSalesTotal = Number(r.transfer?.salesTotal ?? r.transfer?.total) || 0;
            const byAcc = r.transfer?.byAccount || {};
            const accNames = Object.keys(byAcc).filter(n => byAcc[n].total > 0);
            if (tSalesTotal > 0 && accNames.length) {
              accNames.forEach(name => gelirRows.push([`Havale/EFT ${name.replace('HAVALE ', '')}`, Number(byAcc[name].total) || 0]));
            } else if (tSalesTotal > 0) {
              gelirRows.push(['Havale/EFT', tSalesTotal]);
            }
            const tCollTotal = Number(r.transfer?.collectionTotal) || 0;
            const collByAcc = r.transfer?.collectionByAccount || {};
            const collAccNames = Object.keys(collByAcc).filter(n => collByAcc[n].total > 0);
            if (tCollTotal > 0 && collAccNames.length) {
              collAccNames.forEach(name => gelirRows.push([`Tahsilat ${name.replace('HAVALE ', '')}`, Number(collByAcc[name].total) || 0]));
            } else if (tCollTotal > 0) {
              gelirRows.push(['Havale/EFT Tahsilat', tCollTotal]);
            }
            if (Number(r.transfer?.repairTotal) > 0) gelirRows.push(['Havale/EFT Tamirat', Number(r.transfer.repairTotal)]);
            if (Number(r.voucher?.total) > 0) gelirRows.push(['Bakiye', Number(r.voucher.total)]);
            if (Number(r.scrap?.saleLinkedGoldTotal) > 0) gelirRows.push(['Altın Hurda', Number(r.scrap.saleLinkedGoldTotal)]);
            if (Number(r.scrap?.saleLinkedSilverTotal) > 0) gelirRows.push(['Gümüş Hurda', Number(r.scrap.saleLinkedSilverTotal)]);
            if (Number(r.collections?.hurdaTotal) > 0) gelirRows.push(['Hurda ile Tahsilat', Number(r.collections.hurdaTotal)]);

            const gelirTop =
              (Number(r.cash?.total) || 0) + (Number(r.sanalPos?.total) || 0) +
              (Number(r.card?.salesTotal ?? r.card?.total) || 0) + (Number(r.card?.collectionTotal) || 0) + (Number(r.card?.repairTotal) || 0) +
              tSalesTotal + tCollTotal + (Number(r.transfer?.repairTotal) || 0) +
              (Number(r.voucher?.total) || 0) + (Number(r.scrap?.saleLinkedTotal) || 0) + (Number(r.collections?.hurdaTotal) || 0);
            gelirRows.push({ kind: 'total', span: 1, cells: ['TOPLAM GELİR (+)', gelirTop] });

            const giderEntries = [
              ['Kredi Kartı İade', Number(r.cardRefund?.total) || 0],
              ['Nakit İade', Number(r.cashRefund?.total) || 0],
              ['Hurda Nakit İadesi', Number(r.scrapCashRefund?.total) || 0],
              ['Sanal Pos İade', Number(r.sanalPosRefund?.total) || 0],
              ['Değişim Nakit İade', Number(r.exchanges?.cashRefundTotal) || 0],
              ['Değişim Kredi Kartı İade', Number(r.exchanges?.cardRefundTotal) || 0],
              ['Havale İade', Number(r.havaleRefund?.total) || 0],
              ['Değişim Havale İade', Number(r.exchanges?.havaleRefundTotal) || 0],
              ['Masraflar', Number(r.expenses?.total) || 0],
              ['Hurda Alımları', Number(r.scrap?.standaloneTotal) || 0]
            ];
            giderEntries.filter(([, v]) => v > 0).forEach(([label, v]) => giderRows.push([label, -Math.abs(v)]));
            const giderTop = giderEntries.reduce((s, [, v]) => s + (v > 0 ? v : 0), 0);
            giderRows.push({ kind: 'total', span: 1, cells: ['TOPLAM GİDER (-)', -giderTop] });

            sections.push({
              heading: '(+) GELİRLER',
              columns: ['İşlem Adı', 'Tutar (₺)'],
              rows: gelirRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });
            sections.push({
              heading: '(-) GİDERLER',
              columns: ['İşlem Adı', 'Tutar (₺)'],
              rows: giderRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });

            // KASA GENEL TOPLAM + Alt detaylar
            const transferByAccount = r.transfer?.byAccount || {};
            const collectionByAccount = r.transfer?.collectionByAccount || {};
            const repairByAccount = r.transfer?.repairByAccount || {};
            const scrapByAccount = r.scrap?.transferByAccount || {};
            const allAccountNames = [...new Set([...Object.keys(transferByAccount), ...Object.keys(collectionByAccount), ...Object.keys(repairByAccount), ...Object.keys(scrapByAccount)])];
            let netHavaleSirket = 0, netHavaleSahis = 0, netHavaleETicaret = 0;
            allAccountNames.forEach(acc => {
              const accGelir = (transferByAccount[acc]?.total ?? 0) + (collectionByAccount[acc]?.total ?? 0) + (repairByAccount[acc]?.total ?? 0);
              const accGider = scrapByAccount[acc]?.total ?? 0;
              const accNet = accGelir - accGider;
              const accLower = acc.toLocaleLowerCase('tr');
              if (accLower.includes('şahıs') || accLower.includes('sahis') || acc.includes('(Şahıs)')) netHavaleSahis += accNet;
              else if (accLower.includes('e-ticaret') || accLower.includes('eticaret') || acc.includes('E-TİCARET')) netHavaleETicaret += accNet;
              else netHavaleSirket += accNet;
            });
            const netKrediKarti = ((Number(r.card?.salesTotal) || 0) + (Number(r.card?.collectionTotal) || 0) + (Number(r.card?.repairTotal) || 0)) - ((Number(r.cardRefund?.total) || 0) + (Number(r.exchanges?.cardRefundTotal) || 0) + (Number(r.scrap?.cardTotal) || 0));
            const toplamHurda = Number(r.scrap?.total) || 0;

            const altRows = [];
            altRows.push({ kind: 'total', span: 1, cells: ['KASA GENEL TOPLAM', gelirTop - giderTop] });
            if (Number(r.expectedCash) !== 0) altRows.push(['NAKİT KASA TOPLAM', Number(r.expectedCash)]);
            if (netKrediKarti !== 0) altRows.push(['NET KREDİ KARTI', netKrediKarti]);
            if (netHavaleSahis !== 0) altRows.push(['NET HAVALE ŞAHIS', netHavaleSahis]);
            if (netHavaleSirket !== 0) altRows.push(['NET HAVALE ŞİRKET', netHavaleSirket]);
            if (netHavaleETicaret !== 0) altRows.push(['NET HAVALE E-TİCARET', netHavaleETicaret]);
            eTicaretNetRows(r.sanalPos).forEach((row) => altRows.push(row));
            if (toplamHurda !== 0) altRows.push(['TOPLAM HURDA TUTARI', toplamHurda]);
            if (Number(r.openAccount?.total) !== 0) altRows.push(['AÇIK HESAP SATIŞLAR', Number(r.openAccount.total)]);
            if ((Number(r.musteriBakiyesiSales?.total) || 0) !== 0) altRows.push(['MÜŞTERİ BAKİYESİ SATIŞLAR', Number(r.musteriBakiyesiSales?.total || 0)]);
            if ((Number(r.exchanges?.voucherRefundTotal) || 0) + (Number(r.acikHesapSalesRefund?.total) || 0) + (Number(r.bakiyeRefund?.total) || 0) !== 0) altRows.push(['AÇIK HESABA İADELER', Number(r.exchanges?.voucherRefundTotal || 0) + Number(r.acikHesapSalesRefund?.total || 0) + Number(r.bakiyeRefund?.total || 0)]);
            if (Number(r.totalDiscount) !== 0) altRows.push(['İNDİRİMLER TOPLAM', Number(r.totalDiscount)]);

            sections.push({
              heading: 'Genel Toplam & Alt Detaylar',
              columns: ['Kalem', 'Tutar (₺)'],
              rows: altRows,
              numberCols: [1],
              layout: 'kv',
              colWidths: [{ wch: 36 }, { wch: 18 }]
            });

            return {
              title: 'Kasa Defteri Raporu Detaylı',
              periodLabel: r.dateRange ? `${r.dateRange.start} → ${r.dateRange.end} (${r.dateRange.dayCount} gün)` : getPeriodLabel(),
              storeLabel: storeName,
              sections
            };
          };

          const exportCashSumExcel = () => { const p = buildCashSummaryPayload(); if (p) exportSectionedReportToExcel(p); };

          return (
            <div className="tab-content">
              {/* Eylem Çubuğu — dönem ve mağaza filtresi üstteki global seçiciden gelir */}
              {r && (
                <div className="data-card" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <button onClick={exportCashSumExcel} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', background: '#16a34a', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }} title="Excel indir">
                      <Download size={14} /> Excel
                    </button>
                    <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#64748b', fontWeight: 500 }}>
                      {storeName} · {r.dateRange?.dayCount} gün ({r.dateRange?.start} → {r.dateRange?.end})
                    </span>
                  </div>
                </div>
              )}

              {!r ? (
                <div className="data-card"><div className="empty-state"><Wallet size={48} style={{ color: '#9ca3af' }} /><p>{cashReportLoading ? 'Yükleniyor...' : 'Veri bulunamadı'}</p></div></div>
              ) : (
                <div className="kasa-defteri-rapor">
                  {/* Rapor Başlığı */}
                  <div className="kd-header">
                    <table className="kd-header-table">
                      <tbody>
                        <tr><td className="kd-label">Rapor Adı</td><td>: Kasa Defteri Raporu Detaylı</td></tr>
                        <tr><td className="kd-label">Tarih Aralığı</td><td>: {r.dateRange?.start} → {r.dateRange?.end} ({r.dateRange?.dayCount} gün)</td></tr>
                        <tr><td className="kd-label">Mağaza</td><td>: {storeName}</td></tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="kd-store-bar">{storeName}</div>

                  {/* (+) GELİRLER */}
                  <div className="kd-section">
                    <div className="kd-section-title gelir">(+) GELİRLER</div>
                    <div className="kd-table">
                      <div className="kd-table-header">
                        <span>İŞLEM ADI</span>
                        <span>TUTAR</span>
                      </div>
                      {(r.cash?.salesTotal != null ? (Number(r.cash.salesTotal) || 0) : ((Number(r.cash?.total) || 0) - (Number(r.cash?.collectionTotal) || 0) - (Number(r.cash?.repairTotal) || 0))) > 0 && (
                        <div className="kd-row"><span>Nakit Ödeme</span><span>{fmtN(r.cash?.salesTotal != null ? (Number(r.cash.salesTotal) || 0) : ((Number(r.cash?.total) || 0) - (Number(r.cash?.collectionTotal) || 0) - (Number(r.cash?.repairTotal) || 0)))}</span></div>
                      )}
                      {(Number(r.cash?.collectionTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Nakit Tahsilat</span><span>{fmtN(r.cash.collectionTotal)}</span></div>
                      )}
                      {(Number(r.cash?.repairTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Nakit Tamirat</span><span>{fmtN(r.cash.repairTotal)}</span></div>
                      )}
                      {eTicaretGelirRows(r.sanalPos).map(([label, amount]) => (
                        <div key={label} className="kd-row"><span>{label}</span><span>{fmtN(amount)}</span></div>
                      ))}
                      {((Number(r.card?.salesTotal) || 0) + (Number(r.card?.total) || 0)) > 0 && (
                        <div className="kd-row"><span>Kredi Kartı</span><span>{fmtN(r.card.salesTotal ?? r.card.total)}</span></div>
                      )}
                      {(Number(r.card?.collectionTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Kredi Kartı Tahsilat</span><span>{fmtN(r.card.collectionTotal)}</span></div>
                      )}
                      {(Number(r.card?.repairTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Kredi Kartı Tamirat</span><span>{fmtN(r.card.repairTotal)}</span></div>
                      )}
                      {(() => {
                        const tSalesTotal = Number(r.transfer?.salesTotal ?? r.transfer?.total) || 0;
                        const byAcc = r.transfer?.byAccount || {};
                        const accNames = Object.keys(byAcc);
                        if (tSalesTotal > 0 && accNames.length > 0) {
                          return accNames.filter(n => byAcc[n].total > 0).map(name => (
                            <div key={`ts-${name}`} className="kd-row"><span>Havale/EFT {name.replace('HAVALE ', '')}</span><span>{fmtN(byAcc[name].total)}</span></div>
                          ));
                        }
                        if (tSalesTotal > 0) return <div className="kd-row"><span>Havale/EFT</span><span>{fmtN(tSalesTotal)}</span></div>;
                        return null;
                      })()}
                      {(() => {
                        const tCollTotal = Number(r.transfer?.collectionTotal) || 0;
                        const collByAcc = r.transfer?.collectionByAccount || {};
                        const collAccNames = Object.keys(collByAcc);
                        if (tCollTotal > 0 && collAccNames.length > 0) {
                          return collAccNames.filter(n => collByAcc[n].total > 0).map(name => (
                            <div key={`tc-${name}`} className="kd-row"><span>Tahsilat {name.replace('HAVALE ', '')}</span><span>{fmtN(collByAcc[name].total)}</span></div>
                          ));
                        }
                        if (tCollTotal > 0) return <div className="kd-row"><span>Havale/EFT Tahsilat</span><span>{fmtN(tCollTotal)}</span></div>;
                        return null;
                      })()}
                      {(Number(r.transfer?.repairTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Havale/EFT Tamirat</span><span>{fmtN(r.transfer.repairTotal)}</span></div>
                      )}
                      {(Number(r.voucher?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Bakiye</span><span>{fmtN(r.voucher.total)}</span></div>
                      )}
                      {(Number(r.scrap?.saleLinkedGoldTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Altın Hurda</span><span>{fmtN(r.scrap.saleLinkedGoldTotal)}</span></div>
                      )}
                      {(Number(r.scrap?.saleLinkedSilverTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Gümüş Hurda</span><span>{fmtN(r.scrap.saleLinkedSilverTotal)}</span></div>
                      )}
                      {(Number(r.collections?.hurdaTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Hurda ile Tahsilat</span><span>{fmtN(r.collections.hurdaTotal)}</span></div>
                      )}
                      <div className="kd-section-total">
                        TOPLAM : {fmtN(
                          (Number(r.cash?.total) || 0) +
                          (Number(r.sanalPos?.total) || 0) +
                          (Number(r.card?.salesTotal ?? r.card?.total) || 0) +
                          (Number(r.card?.collectionTotal) || 0) +
                          (Number(r.card?.repairTotal) || 0) +
                          (Number(r.transfer?.salesTotal ?? r.transfer?.total) || 0) +
                          (Number(r.transfer?.collectionTotal) || 0) +
                          (Number(r.transfer?.repairTotal) || 0) +
                          (Number(r.voucher?.total) || 0) +
                          (Number(r.scrap?.saleLinkedTotal) || 0) +
                          (Number(r.collections?.hurdaTotal) || 0)
                        )} (+)
                      </div>
                    </div>
                  </div>

                  {/* (-) GİDERLER */}
                  <div className="kd-section">
                    <div className="kd-section-title gider">(-) GİDERLER</div>
                    <div className="kd-table">
                      <div className="kd-table-header">
                        <span>İŞLEM ADI</span>
                        <span>TUTAR</span>
                      </div>
                      {(Number(r.cardRefund?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Kredi Kartı İade</span><span>-{fmtN(r.cardRefund.total)}</span></div>
                      )}
                      {(Number(r.cashRefund?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Nakit İade</span><span>-{fmtN(r.cashRefund.total)}</span></div>
                      )}
                      {(Number(r.scrapCashRefund?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Hurda Nakit İadesi</span><span>-{fmtN(r.scrapCashRefund.total)}</span></div>
                      )}
                      {(Number(r.sanalPosRefund?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Sanal Pos İade</span><span>-{fmtN(r.sanalPosRefund.total)}</span></div>
                      )}
                      {(Number(r.exchanges?.cashRefundTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Değişim Nakit İade</span><span>-{fmtN(r.exchanges.cashRefundTotal)}</span></div>
                      )}
                      {(Number(r.exchanges?.cardRefundTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Değişim Kredi Kartı İade</span><span>-{fmtN(r.exchanges.cardRefundTotal)}</span></div>
                      )}
                      {(Number(r.havaleRefund?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Havale İade</span><span>-{fmtN(r.havaleRefund.total)}</span></div>
                      )}
                      {(Number(r.exchanges?.havaleRefundTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Değişim Havale İade</span><span>-{fmtN(r.exchanges.havaleRefundTotal)}</span></div>
                      )}
                      {(Number(r.expenses?.total) || 0) > 0 && (
                        <div className="kd-row"><span>Masraflar</span><span>-{fmtN(r.expenses.total)}</span></div>
                      )}
                      {(Number(r.scrap?.standaloneTotal) || 0) > 0 && (
                        <div className="kd-row"><span>Hurda Alımları</span><span>-{fmtN(r.scrap.standaloneTotal)}</span></div>
                      )}
                      {(() => {
                        const giderTop = (Number(r.cardRefund?.total) || 0) + (Number(r.cashRefund?.total) || 0) + (Number(r.scrapCashRefund?.total) || 0) + (Number(r.sanalPosRefund?.total) || 0) + (Number(r.havaleRefund?.total) || 0) + (Number(r.exchanges?.cashRefundTotal) || 0) + (Number(r.exchanges?.cardRefundTotal) || 0) + (Number(r.exchanges?.havaleRefundTotal) || 0) + (Number(r.expenses?.total) || 0) + (Number(r.scrap?.standaloneTotal) || 0);
                        return giderTop === 0 ? <div className="kd-row"><span style={{ color: '#999' }}>Gider bulunmuyor</span><span>0,00</span></div> : null;
                      })()}
                      <div className="kd-section-total">
                        TOPLAM : {fmtN(
                          (Number(r.cardRefund?.total) || 0) +
                          (Number(r.cashRefund?.total) || 0) +
                          (Number(r.scrapCashRefund?.total) || 0) +
                          (Number(r.sanalPosRefund?.total) || 0) +
                          (Number(r.havaleRefund?.total) || 0) +
                          (Number(r.exchanges?.cashRefundTotal) || 0) +
                          (Number(r.exchanges?.cardRefundTotal) || 0) +
                          (Number(r.exchanges?.havaleRefundTotal) || 0) +
                          (Number(r.expenses?.total) || 0) +
                          (Number(r.scrap?.standaloneTotal) || 0)
                        )} (-)
                      </div>
                    </div>
                  </div>

                  {/* KASA GENEL TOPLAM */}
                  {(() => {
                    const gelirTop = (Number(r.cash?.total) || 0) + (Number(r.sanalPos?.total) || 0) + (Number(r.card?.salesTotal ?? r.card?.total) || 0) + (Number(r.card?.collectionTotal) || 0) + (Number(r.card?.repairTotal) || 0) + (Number(r.transfer?.salesTotal ?? r.transfer?.total) || 0) + (Number(r.transfer?.collectionTotal) || 0) + (Number(r.transfer?.repairTotal) || 0) + (Number(r.voucher?.total) || 0) + (Number(r.scrap?.saleLinkedTotal) || 0) + (Number(r.collections?.hurdaTotal) || 0);
                    const giderTop = (Number(r.cardRefund?.total) || 0) + (Number(r.cashRefund?.total) || 0) + (Number(r.scrapCashRefund?.total) || 0) + (Number(r.sanalPosRefund?.total) || 0) + (Number(r.havaleRefund?.total) || 0) + (Number(r.exchanges?.cashRefundTotal) || 0) + (Number(r.exchanges?.cardRefundTotal) || 0) + (Number(r.exchanges?.havaleRefundTotal) || 0) + (Number(r.expenses?.total) || 0) + (Number(r.scrap?.standaloneTotal) || 0);
                    const kasaGenel = gelirTop - giderTop;
                    return (
                      <div className="kd-genel-toplam">
                        KASA GENEL TOPLAM : &nbsp; <strong>{fmtN(kasaGenel)}</strong> {kasaGenel >= 0 ? '(+)' : '(-)'}
                      </div>
                    );
                  })()}

                  {/* Alt Detaylar */}
                  {(() => {
                    const transferByAccount = r.transfer?.byAccount || {};
                    const collectionByAccount = r.transfer?.collectionByAccount || {};
                    const repairByAccount = r.transfer?.repairByAccount || {};
                    const scrapByAccount = r.scrap?.transferByAccount || {};
                    const allAccountNames = [...new Set([...Object.keys(transferByAccount), ...Object.keys(collectionByAccount), ...Object.keys(repairByAccount), ...Object.keys(scrapByAccount)])];
                    let netHavaleSirket = 0, netHavaleSahis = 0, netHavaleETicaret = 0;
                    allAccountNames.forEach(acc => {
                      const accGelir = (transferByAccount[acc]?.total ?? 0) + (collectionByAccount[acc]?.total ?? 0) + (repairByAccount[acc]?.total ?? 0);
                      const accGider = scrapByAccount[acc]?.total ?? 0;
                      const accNet = accGelir - accGider;
                      const accLower = acc.toLocaleLowerCase('tr');
                      if (accLower.includes('şahıs') || accLower.includes('sahis') || acc.includes('(Şahıs)')) netHavaleSahis += accNet;
                      else if (accLower.includes('e-ticaret') || accLower.includes('eticaret') || acc.includes('E-TİCARET')) netHavaleETicaret += accNet;
                      else netHavaleSirket += accNet;
                    });
                    const netKrediKarti = ((Number(r.card?.salesTotal) || 0) + (Number(r.card?.collectionTotal) || 0) + (Number(r.card?.repairTotal) || 0)) - ((Number(r.cardRefund?.total) || 0) + (Number(r.exchanges?.cardRefundTotal) || 0) + (Number(r.scrap?.cardTotal) || 0));
                    const toplamHurda = Number(r.scrap?.total) || 0;
                    return (
                      <div className="kd-alt-detaylar">
                        {(Number(r.expectedCash) || 0) !== 0 && <div className="kd-alt-row">NAKİT KASA TOPLAM : {fmtN(r.expectedCash)} {(r.expectedCash || 0) >= 0 ? '(+)' : '(-)'}</div>}
                        {netKrediKarti !== 0 && <div className="kd-alt-row">NET KREDİ KARTI : {fmtN(netKrediKarti)} {netKrediKarti >= 0 ? '(+)' : '(-)'}</div>}
                        {netHavaleSahis !== 0 && <div className="kd-alt-row">NET HAVALE ŞAHIS : {fmtN(netHavaleSahis)} {netHavaleSahis >= 0 ? '(+)' : '(-)'}</div>}
                        {netHavaleSirket !== 0 && <div className="kd-alt-row">NET HAVALE ŞİRKET : {fmtN(netHavaleSirket)} {netHavaleSirket >= 0 ? '(+)' : '(-)'}</div>}
                        {netHavaleETicaret !== 0 && <div className="kd-alt-row">NET HAVALE E-TİCARET : {fmtN(netHavaleETicaret)} {netHavaleETicaret >= 0 ? '(+)' : '(-)'}</div>}
                        {eTicaretNetRows(r.sanalPos).map(([label, amount]) => <div key={label} className="kd-alt-row">{label} : {fmtN(amount)} {amount >= 0 ? '(+)' : '(-)'}</div>)}
                        {toplamHurda !== 0 && <div className="kd-alt-row">TOPLAM HURDA TUTARI : {fmtN(toplamHurda)}</div>}
                        {(Number(r.openAccount?.total) || 0) !== 0 && <div className="kd-alt-row">AÇIK HESAP SATIŞLAR : {fmtN(r.openAccount.total)}</div>}
                        {(Number(r.musteriBakiyesiSales?.total) || 0) !== 0 && <div className="kd-alt-row">MÜŞTERİ BAKİYESİ SATIŞLAR : {fmtN(r.musteriBakiyesiSales?.total)}</div>}
                        {((Number(r.exchanges?.voucherRefundTotal) || 0) + (Number(r.acikHesapSalesRefund?.total) || 0) + (Number(r.bakiyeRefund?.total) || 0)) !== 0 && <div className="kd-alt-row">AÇIK HESABA İADELER : {fmtN(Number(r.exchanges?.voucherRefundTotal || 0) + Number(r.acikHesapSalesRefund?.total || 0) + Number(r.bakiyeRefund?.total || 0))}</div>}
                        {(Number(r.totalDiscount) || 0) !== 0 && <div className="kd-alt-row">İNDİRİMLER TOPLAM : {fmtN(r.totalDiscount)}</div>}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        })()}

        {/* ÜRÜN ANALİZİ TAB */}
        {activeTab === 'products' && (
          <div className="tab-content">
            {/* En Çok Satan Ürünler */}
            <div className="data-card">
              <h3>En Çok Satan Ürünler</h3>
              {productStats.length > 0 ? (
                <>
                  <div className="top-products-grid">
                    {productStats.slice(0, 10).map((product, index) => (
                      <div key={product.product_id || index} className={`product-rank-card ${index < 3 ? 'top-3' : ''}`}>
                        <div className="rank-badge">
                          {index === 0 ? <Trophy size={20} style={{ color: '#f59e0b' }} /> : index === 1 ? <Medal size={20} style={{ color: '#9ca3af' }} /> : index === 2 ? <Award size={20} style={{ color: '#cd7f32' }} /> : `#${index + 1}`}
                        </div>
                        <div className="product-details">
                          <div className="product-name">{product.product_name}</div>
                          <div className="product-category">{product.category || 'Kategori Yok'}</div>
                          <div className="product-stats">
                            <span>{product.total_quantity} adet</span>
                            <span className="revenue">{formatCurrency(product.total_revenue)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tüm Ürün Listesi */}
                  <div className="table-wrapper" style={{ marginTop: '20px' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Sıra</th>
                          <th>Fotoğraf</th>
                          <th>Ürün</th>
                          <th>Kategori</th>
                          {[
                            { field: 'quantity', label: 'Satış Adedi' },
                            { field: 'revenue', label: 'Ciro' },
                            { field: 'avg_price', label: 'Ort. Fiyat' },
                            { field: 'last_sale', label: 'Son Satış' },
                          ].map(col => (
                            <th
                              key={col.field}
                              onClick={() => {
                                if (productSortField === col.field) {
                                  setProductSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
                                } else {
                                  setProductSortField(col.field);
                                  setProductSortDir('desc');
                                }
                              }}
                              style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            >
                              {col.label}{' '}
                              {productSortField === col.field
                                ? (productSortDir === 'desc' ? <ArrowDown size={14} style={{ verticalAlign: 'middle' }} /> : <ArrowUp size={14} style={{ verticalAlign: 'middle' }} />)
                                : <ArrowUpDown size={14} style={{ verticalAlign: 'middle', opacity: 0.3 }} />}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sorted = productSortField
                            ? [...productStats].sort((a, b) => {
                                let valA, valB;
                                switch (productSortField) {
                                  case 'quantity': valA = Number(a.total_quantity) || 0; valB = Number(b.total_quantity) || 0; break;
                                  case 'revenue': valA = Number(a.total_revenue) || 0; valB = Number(b.total_revenue) || 0; break;
                                  case 'avg_price': valA = Number(a.avg_price) || 0; valB = Number(b.avg_price) || 0; break;
                                  case 'last_sale': valA = a.last_sale_date ? new Date(a.last_sale_date).getTime() : 0; valB = b.last_sale_date ? new Date(b.last_sale_date).getTime() : 0; break;
                                  default: valA = 0; valB = 0;
                                }
                                return productSortDir === 'desc' ? valB - valA : valA - valB;
                              })
                            : productStats;
                          return sorted.map((product, index) => (
                            <tr key={product.product_id || index}>
                              <td>#{index + 1}</td>
                              <td style={{ textAlign: 'center' }}>
                                {product.image ? (
                                  <img
                                    src={`${API_HOST}${product.image}`}
                                    alt={product.product_name}
                                    style={{ width: '44px', height: '44px', borderRadius: '6px', objectFit: 'cover', cursor: 'pointer' }}
                                    onClick={() => setReportImageModal({ src: `${API_HOST}${product.image}`, name: product.product_name })}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ) : (
                                  <div style={{ width: '44px', height: '44px', borderRadius: '6px', background: '#f1f5f9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Package size={18} style={{ color: '#94a3b8' }} />
                                  </div>
                                )}
                              </td>
                              <td>
                                <div>
                                  <span className="product-name">{product.product_name}</span>
                                  {product.barcode && <small className="product-barcode" style={{ display: 'block' }}>{product.barcode}</small>}
                                </div>
                              </td>
                              <td>{product.category || '-'}</td>
                              <td>{product.total_quantity}</td>
                              <td className="amount">{formatCurrency(product.total_revenue)}</td>
                              <td>{formatCurrency(product.avg_price)}</td>
                              <td>{product.last_sale_date ? new Date(product.last_sale_date).toLocaleDateString('tr-TR') : '-'}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <Package size={48} style={{ color: '#9ca3af' }} />
                  <p>Bu dönemde ürün satışı bulunamadı</p>
                </div>
              )}
            </div>

            {/* Uzun Süredir Satılmayan Ürünler */}
            <div className="data-card" style={{ marginTop: '24px' }}>
              <div className="card-header-with-filter">
                <h3>Uzun Süredir Satılmayan Ürünler</h3>
                <div className="days-filter">
                  <label>Son</label>
                  <select
                    value={notSoldDays}
                    onChange={(e) => setNotSoldDays(parseInt(e.target.value))}
                    className="days-select"
                  >
                    <option value="1">1 gün</option>
                    <option value="3">3 gün</option>
                    <option value="7">7 gün</option>
                    <option value="14">14 gün</option>
                    <option value="30">30 gün</option>
                    <option value="60">60 gün</option>
                    <option value="90">90 gün</option>
                    <option value="180">6 ay</option>
                    <option value="365">1 yıl</option>
                  </select>
                  <span>dir satılmayanlar</span>
                </div>
              </div>
              {notSoldProducts.length > 0 ? (
                <div className="table-wrapper">
                  <table className="data-table not-sold-table">
                    <thead>
                      <tr>
                        <th>Fotoğraf</th>
                        <th>Ürün</th>
                        <th>Kategori</th>
                        <th>Barkod</th>
                        <th>Stok</th>
                        <th>Fiyat</th>
                        <th>Son Satış</th>
                        <th>Gün</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notSoldProducts.map((product) => (
                        <tr key={product.id} className={product.days_since_sale > 90 ? 'warning-row' : ''}>
                          <td style={{ textAlign: 'center' }}>
                            {product.image ? (
                              <img
                                src={`${API_HOST}${product.image}`}
                                alt={product.name}
                                style={{ width: '44px', height: '44px', borderRadius: '6px', objectFit: 'cover', cursor: 'pointer' }}
                                onClick={() => setReportImageModal({ src: `${API_HOST}${product.image}`, name: product.name })}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div style={{ width: '44px', height: '44px', borderRadius: '6px', background: '#f1f5f9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Package size={18} style={{ color: '#94a3b8' }} />
                              </div>
                            )}
                          </td>
                          <td className="product-name">{product.name}</td>
                          <td>{product.category || '-'}</td>
                          <td><code>{product.barcode || '-'}</code></td>
                          <td>{product.stock}</td>
                          <td>
                            {product.currency === 'USD' ? `$${parseFloat(product.price).toLocaleString('en-US')}` :
                             product.currency === 'EUR' ? `€${parseFloat(product.price).toLocaleString('de-DE')}` :
                             formatCurrency(product.price)}
                          </td>
                          <td>
                            {product.last_sale_date
                              ? new Date(product.last_sale_date).toLocaleDateString('tr-TR')
                              : <span className="never-sold">Hiç satılmadı</span>
                            }
                          </td>
                          <td>
                            <span className={`days-badge ${product.days_since_sale > 90 ? 'danger' : product.days_since_sale > 30 ? 'warning' : ''}`}>
                              {product.days_since_sale} gün
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state success">
                  <CheckCircle size={48} style={{ color: '#10b981' }} />
                  <p>Son {notSoldDays} gün içinde tüm ürünler satılmış!</p>
                </div>
              )}
              <div className="not-sold-summary">
                <span>{notSoldProducts.length} ürün {notSoldDays} gündür satılmıyor</span>
                {notSoldProducts.length > 0 && (
                  <span className="total-value">
                    Toplam Stok Değeri: {formatCurrency(notSoldProducts.reduce((sum, p) => sum + (p.price * p.stock), 0))}
                  </span>
                )}
              </div>
            </div>

            {/* Fotoğraf Büyütme Modal */}
            {reportImageModal && (
              <div
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.8)', zIndex: 9999,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setReportImageModal(null)}
              >
                <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                  <img
                    src={reportImageModal.src}
                    alt={reportImageModal.name}
                    style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
                  />
                  <div style={{ textAlign: 'center', color: '#fff', marginTop: '12px', fontSize: '16px', fontWeight: 500 }}>
                    {reportImageModal.name}
                  </div>
                  <button
                    onClick={() => setReportImageModal(null)}
                    style={{
                      position: 'absolute', top: '-12px', right: '-12px',
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: '#fff', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '20px', fontWeight: 'bold', color: '#333',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* KATEGORİ ANALİZİ TAB */}
        {activeTab === 'categories' && (
          <div className="tab-content">
            {/* Kategori Özet Kartları */}
            <div className="stats-grid">
              {categoryStats.slice(0, 4).map((cat, index) => (
                <div key={index} className={`stat-card ${index === 0 ? 'primary' : ''}`}>
                  <div className="stat-icon"><FolderOpen size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} /></div>
                  <div className="stat-info">
                    <div className="stat-value">{formatCurrency(cat.total_revenue)}</div>
                    <div className="stat-label">{cat.category}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="chart-card">
              <h3>Kategorilere Göre Satış Dağılımı</h3>
              {categoryStats.length > 0 ? (
                <div className="category-bars">
                  {categoryStats.map((cat, index) => (
                    <div key={index} className="category-bar-item">
                      <div className="category-info">
                        <span className="category-name">{cat.category}</span>
                        <span className="category-revenue">{formatCurrency(cat.total_revenue)}</span>
                      </div>
                      <div className="category-bar-container">
                        <div
                          className={`category-bar color-${(index % 5) + 1}`}
                          style={{ width: `${cat.percent}%` }}
                        ></div>
                        <span className="category-percent">{cat.percent}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <FolderOpen size={48} style={{ color: '#9ca3af' }} />
                  <p>Bu dönemde kategori verisi bulunamadı</p>
                </div>
              )}
            </div>

            {/* Kategori Detay Tablosu */}
            {categoryStats.length > 0 && (
              <div className="data-card" style={{ marginTop: '20px' }}>
                <h3>Kategori Detayları</h3>
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Kategori</th>
                        <th>Ürün Çeşidi</th>
                        <th>Satış Adedi</th>
                        <th>İşlem Sayısı</th>
                        <th>Toplam Ciro</th>
                        <th>Oran</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryStats.map((cat, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`category-badge color-${(index % 5) + 1}`}>
                              {cat.category}
                            </span>
                          </td>
                          <td>{cat.product_count}</td>
                          <td>{cat.total_quantity}</td>
                          <td>{cat.sale_count}</td>
                          <td className="amount">{formatCurrency(cat.total_revenue)}</td>
                          <td>
                            <div className="percent-bar-mini">
                              <div className="percent-fill" style={{ width: `${cat.percent}%` }}></div>
                              <span>%{cat.percent}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ÖDEME YÖNTEMLERİ TAB */}
        {activeTab === 'payments' && (
          <div className="tab-content">
            <div className="stats-grid">
              {paymentStats.map((payment, index) => (
                <div key={index} className={`stat-card ${index === 0 ? 'primary' : ''}`}>
                  <div className="stat-icon">
                    {payment.name === 'Nakit' ? <Banknote size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} /> :
                     payment.name === 'Kredi Kartı' ? <CreditCard size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} /> :
                     payment.name === 'Havale/EFT' ? <Building2 size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} /> : <Coins size={16} style={{ color: index === 0 ? '#fff' : '#0f766e' }} />}
                  </div>
                  <div className="stat-info">
                    <div className="stat-value">{formatCurrency(payment.revenue)}</div>
                    <div className="stat-label">{payment.name}</div>
                  </div>
                </div>
              ))}
            </div>

            {paymentStats.length > 0 && (
              <div className="chart-card">
                <h3>Ödeme Yöntemi Dağılımı</h3>
                <div className="pie-legend">
                  {paymentStats.map((p, index) => (
                    <div key={index} className="pie-legend-item">
                      <span className={`pie-color color-${(index % 5) + 1}`}></span>
                      <span className="pie-label">{p.name}</span>
                      <span className="pie-value">{p.percent}%</span>
                    </div>
                  ))}
                </div>
                <div className="horizontal-bars">
                  {paymentStats.map((p, index) => {
                    const maxRevenue = paymentStats[0]?.revenue || 1;
                    return (
                      <div key={index} className="h-bar-item">
                        <div className="h-bar-label">{p.name}</div>
                        <div className="h-bar-container">
                          <div
                            className={`h-bar color-${(index % 5) + 1}`}
                            style={{ width: `${(p.revenue / maxRevenue) * 100}%` }}
                          ></div>
                          <span className="h-bar-value">{formatCurrency(p.revenue)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {paymentStats.length === 0 && (
              <div className="empty-state">
                <CreditCard size={48} style={{ color: '#9ca3af' }} />
                <p>Bu dönemde ödeme verisi bulunamadı</p>
              </div>
            )}
          </div>
        )}

        {/* SARF MALZEMELERİ TAB */}
        {activeTab === 'consumables' && (
          <div className="tab-content">
            {consumableLoading ? (
              <Loading text="Sarf malzemeleri yükleniyor..." size="medium" />
            ) : consumableStats ? (
              <>
                {/* Özet İstatistikler */}
                <div className="stats-grid">
                  <div className="stat-card primary">
                    <div className="stat-icon"><BarChart3 size={16} /></div>
                    <div className="stat-info">
                      <div className="stat-value">{consumableStats.summary?.total_used || 0}</div>
                      <div className="stat-label">Toplam Kullanım</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><Package size={16} style={{ color: '#0f766e' }} /></div>
                    <div className="stat-info">
                      <div className="stat-value">{consumableStats.summary?.total_products || 0}</div>
                      <div className="stat-label">Ürün Çeşidi</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><AlertTriangle size={16} style={{ color: '#f59e0b' }} /></div>
                    <div className="stat-info">
                      <div className="stat-value">{consumableStats.summary?.low_stock_count || 0}</div>
                      <div className="stat-label">Azalan Stok</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon"><XCircle size={16} style={{ color: '#ef4444' }} /></div>
                    <div className="stat-info">
                      <div className="stat-value">{consumableStats.summary?.out_of_stock_count || 0}</div>
                      <div className="stat-label">Stokta Yok</div>
                    </div>
                  </div>
                </div>

                {/* En Çok Kullanılan Sarf Malzemeleri */}
                <div className="chart-card">
                  <h3>En Çok Kullanılan Sarf Malzemeleri</h3>
                  {consumableStats.top_used && consumableStats.top_used.length > 0 ? (
                    <div className="horizontal-bars">
                      {consumableStats.top_used.slice(0, 10).map((item, index) => {
                        const maxUsage = consumableStats.top_used[0]?.total_used || 1;
                        return (
                          <div key={item.product_id} className="h-bar-item consumable-bar-item">
                            <div className="h-bar-label">{item.product_name}</div>
                            <div className="h-bar-container">
                              <div
                                className={`h-bar color-${(index % 5) + 1}`}
                                style={{ width: `${(item.total_used / maxUsage) * 100}%` }}
                              ></div>
                              <span className="h-bar-value">
                                {item.total_used} adet
                                <span className="h-bar-meta"> · Günlük: {item.daily_average?.toFixed(1) || 0}</span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <Package size={48} style={{ color: '#9ca3af' }} />
                      <p>Henüz sarf malzemesi kullanımı yok</p>
                    </div>
                  )}
                </div>

                {/* Kategori Bazlı Kullanım */}
                {consumableStats.category_stats && consumableStats.category_stats.length > 0 && (
                  <div className="chart-card">
                    <h3>Kategori Bazlı Kullanım</h3>
                    <div className="category-bars">
                      {consumableStats.category_stats.map((cat, index) => {
                        const maxUsage = consumableStats.category_stats[0]?.total_used || 1;
                        const percent = ((cat.total_used / consumableStats.summary?.total_used) * 100).toFixed(1);
                        return (
                          <div key={cat.category} className="category-bar-item">
                            <div className="category-info">
                              <span className="category-name">{cat.category}</span>
                              <span className="category-revenue">{cat.total_used} adet</span>
                            </div>
                            <div className="category-bar-container">
                              <div
                                className={`category-bar color-${(index % 5) + 1}`}
                                style={{ width: `${percent}%` }}
                              ></div>
                              <span className="category-percent">{percent}%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Stok Tahmini Tablosu */}
                <div className="data-card">
                  <h3>Stok Tükenme Tahmini</h3>
                  {consumableStats.stock_forecast && consumableStats.stock_forecast.length > 0 ? (
                    <div className="table-wrapper">
                      <table className="data-table consumable-forecast-table">
                        <thead>
                          <tr>
                            <th>Ürün</th>
                            <th>Kategori</th>
                            <th>Mevcut Stok</th>
                            <th>Günlük Ort.</th>
                            <th>Kalan Gün</th>
                            <th>Tahmini Bitiş</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {consumableStats.stock_forecast.map((item) => {
                            const daysRemaining = item.days_remaining;
                            let statusClass = '';
                            let statusText = '';

                            if (item.current_stock === 0) {
                              statusClass = 'danger';
                              statusText = 'Stokta Yok';
                            } else if (daysRemaining !== null && daysRemaining <= 7) {
                              statusClass = 'danger';
                              statusText = 'Kritik';
                            } else if (daysRemaining !== null && daysRemaining <= 14) {
                              statusClass = 'warning';
                              statusText = 'Azalıyor';
                            } else if (daysRemaining === null || item.daily_average === 0) {
                              statusClass = 'neutral';
                              statusText = 'Kullanım Yok';
                            } else {
                              statusClass = 'success';
                              statusText = 'Yeterli';
                            }

                            return (
                              <tr key={item.product_id} className={item.current_stock === 0 ? 'warning-row' : ''}>
                                <td className="product-name">{item.product_name}</td>
                                <td>{item.category || '-'}</td>
                                <td className={item.current_stock < 10 ? 'low-stock' : ''}>{item.current_stock}</td>
                                <td>{item.daily_average?.toFixed(2) || '-'}</td>
                                <td>
                                  {daysRemaining !== null ? (
                                    <span className={`days-badge ${daysRemaining <= 7 ? 'danger' : daysRemaining <= 14 ? 'warning' : ''}`}>
                                      {daysRemaining} gün
                                    </span>
                                  ) : '-'}
                                </td>
                                <td>
                                  {item.estimated_depletion_date ? (
                                    new Date(item.estimated_depletion_date).toLocaleDateString('tr-TR')
                                  ) : '-'}
                                </td>
                                <td>
                                  <span className={`status-badge-consumable ${statusClass}`}>
                                    {statusText}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <Package size={48} style={{ color: '#9ca3af' }} />
                      <p>Sarf malzemesi bulunamadı</p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-state">
                <Package size={48} style={{ color: '#9ca3af' }} />
                <p>Sarf malzemeleri verisi yüklenemedi</p>
              </div>
            )}
          </div>
        )}

        {/* MÜŞTERİ ANALİZİ TAB */}
        {activeTab === 'customers' && (
          <CustomerAnalysis getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* YAŞLANAN STOK TAB */}
        {activeTab === 'aging' && (
          <AgingInventory formatCurrency={formatCurrency} />
        )}

        {/* YOĞUNLUK HARİTASI TAB */}
        {activeTab === 'heatmap' && (
          <SalesHeatmap getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* TAMİR SERVİSİ TAB */}
        {activeTab === 'repairs' && (
          <RepairReport getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* HURDA ALIM TAB */}
        {activeTab === 'scrap' && (
          <ScrapReport getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* GİDER ANALİZİ TAB */}
        {activeTab === 'expenses' && (
          <ExpenseAnalysis getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* MAĞAZA KARŞILAŞTIRMA TAB */}
        {activeTab === 'comparison' && (
          <StoreComparison getDateRange={getDateRange} formatCurrency={formatCurrency} />
        )}

        {/* TREND ANALİZİ TAB */}
        {activeTab === 'trends' && (
          <TrendAnalysis getDateRange={getDateRange} reportStoreFilter={reportStoreFilter} formatCurrency={formatCurrency} />
        )}

        {/* ÖZEL RAPOR TAB */}
        {activeTab === 'customReport' && (
          <CustomReport
            getDateRange={getDateRange}
            reportStoreFilter={reportStoreFilter}
            formatCurrency={formatCurrency}
            getPeriodLabel={getPeriodLabel}
            getStoreLabel={getStoreLabel}
          />
        )}

        {/* KAMPANYA PERFORMANSI TAB */}
        {activeTab === 'campaigns' && (
          <CampaignPerformance getDateRange={getDateRange} formatCurrency={formatCurrency} />
        )}

      </div>
      )}

      {/* Personel Detay Modal */}
      {showStaffModal && selectedStaff && (
        <div className="modal-overlay" onClick={() => setShowStaffModal(false)}>
          <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="staff-modal-header">
              <h2>
                <Users size={20} />
                {selectedStaff.staffName} - Satış Detayları
              </h2>
              <button className="close-btn" onClick={() => setShowStaffModal(false)}>×</button>
            </div>

            <div className="staff-modal-summary">
              <div className="summary-item">
                <span className="label">Toplam Satış</span>
                <span className="value">{selectedStaff.orderCount}</span>
              </div>
              <div className="summary-item">
                <span className="label">Toplam Ciro</span>
                <span className="value">{formatCurrency(selectedStaff.totalRevenue)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Ortalama Sepet</span>
                <span className="value">{formatCurrency(selectedStaff.avgOrder)}</span>
              </div>
              <div className="summary-item highlight">
                <span className="label">Prim (%2)</span>
                <span className="value">{formatCurrency(selectedStaff.commission)}</span>
              </div>
            </div>

            <div className="staff-modal-content">
              <h3>İşlem Listesi ({getStaffSales(selectedStaff.staffName).length} kayıt)</h3>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Fiş No</th>
                      <th>Tarih</th>
                      <th>Tür</th>
                      <th>Müşteri</th>
                      <th>Ürün Sayısı</th>
                      <th>Tutar</th>
                      <th>Ödeme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getStaffSales(selectedStaff.staffName).map(sale => {
                      const isExchangeSale = !!exchangeLinkage.exchangeSaleInfo[sale.id];
                      const staffAmount = getStaffDisplayAmount(sale);
                      const displayType = isExchangeSale ? 'exchange' : sale.record_type;
                      return (
                      <tr key={sale.exchange_id ? `exc-${sale.exchange_id}` : sale.id} className={displayType === 'refund' ? 'refund-row' : displayType === 'exchange' ? 'exchange-row' : ''}>
                        <td className="receipt-no">#{sale.receipt_number || sale.store_sale_number || sale.id}</td>
                        <td>{new Date(sale.created_at).toLocaleString('tr-TR')}</td>
                        <td>
                          <span className={`type-badge ${displayType}`}>
                            {isExchangeSale ? 'Değişim' :
                             sale.record_type === 'sale' ? 'Satış' :
                             sale.record_type === 'refund' ? 'İade' :
                             sale.record_type === 'exchange' ? 'Değişim' : 'Satış'}
                          </span>
                        </td>
                        <td>{sale.customer_name || '-'}</td>
                        <td>{sale.items?.length || 0}</td>
                        <td className={`amount ${displayType === 'refund' ? 'refund-amount' : displayType === 'exchange' ? 'exchange-amount' : ''}`}>
                          {sale.record_type === 'refund' ? '-' : ''}{formatCurrency(Math.abs(staffAmount))}
                        </td>
                        <td><span className={`badge ${sale.payment_method?.toLowerCase().replace(/[^a-z]/g, '-')}`}>{sale.payment_method}</span></td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Reports;
